import { useState, useEffect, useRef } from 'react';

// ─── GLOBAL STYLES ───────────────────────────────────────────────────────────
const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap');
    
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    html { scroll-behavior: smooth; }
    body { background: #fff; font-family: 'Poppins', sans-serif; color: #0a0a0a; overflow-x: hidden; }

    :root {
      --black: #0a0a0a;
      --white: #ffffff;
      --gray-50: #f9f9f9;
      --gray-100: #f3f3f3;
      --gray-200: #e8e8e8;
      --gray-400: #9ca3af;
      --gray-500: #6b7280;
      --gray-700: #374151;
      --accent: #1a56db;
      --accent-dark: #1040b0;
      --accent-light: #eef2ff;
    }

    button { font-family: 'Poppins', sans-serif; }

    .btn-primary {
      background: var(--black);
      color: var(--white);
      border: none;
      padding: 14px 32px;
      font-size: 13px;
      font-weight: 600;
      letter-spacing: 0.5px;
      cursor: pointer;
      transition: all 0.2s ease;
      font-family: 'Poppins', sans-serif;
    }
    .btn-primary:hover { background: var(--accent); transform: translateY(-1px); }

    .btn-outline {
      background: transparent;
      color: var(--black);
      border: 1.5px solid var(--black);
      padding: 13px 30px;
      font-size: 13px;
      font-weight: 600;
      letter-spacing: 0.5px;
      cursor: pointer;
      transition: all 0.2s ease;
      font-family: 'Poppins', sans-serif;
    }
    .btn-outline:hover { background: var(--black); color: var(--white); }

    .btn-outline-white {
      background: transparent;
      color: var(--white);
      border: 1.5px solid rgba(255,255,255,0.4);
      padding: 13px 30px;
      font-size: 13px;
      font-weight: 600;
      letter-spacing: 0.5px;
      cursor: pointer;
      transition: all 0.2s ease;
      font-family: 'Poppins', sans-serif;
    }
    .btn-outline-white:hover { border-color: var(--white); background: rgba(255,255,255,0.08); }

    .section-label {
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 2.5px;
      text-transform: uppercase;
      color: var(--accent);
      margin-bottom: 14px;
    }

    .section-title {
      font-size: clamp(24px, 3.5vw, 46px);
      font-weight: 700;
      line-height: 1.12;
      letter-spacing: -0.8px;
      color: var(--black);
    }

    .container {
      max-width: 1240px;
      margin: 0 auto;
      padding: 0 40px;
    }

    input, textarea, select {
      font-family: 'Poppins', sans-serif;
    }

    @keyframes fadeUp {
      from { opacity: 0; transform: translateY(20px); }
      to { opacity: 1; transform: translateY(0); }
    }
    .fade-up { animation: fadeUp 0.5s ease forwards; }

    /* Mobile drawer */
    @keyframes slideIn {
      from { transform: translateX(100%); }
      to { transform: translateX(0); }
    }
    @keyframes slideOut {
      from { transform: translateX(0); }
      to { transform: translateX(100%); }
    }

    /* Responsive breakpoints */
    @media (max-width: 1024px) {
      .container { padding: 0 24px; }
    }

    @media (max-width: 768px) {
      .container { padding: 0 16px; }
      .btn-primary, .btn-outline { padding: 12px 24px; font-size: 12px; }
      .btn-outline-white { padding: 12px 24px; font-size: 12px; }
    }

    @media (max-width: 480px) {
      .container { padding: 0 14px; }
    }

    /* Scrollbar */
    ::-webkit-scrollbar { width: 6px; }
    ::-webkit-scrollbar-track { background: #f1f1f1; }
    ::-webkit-scrollbar-thumb { background: #c1c1c1; border-radius: 3px; }
    ::-webkit-scrollbar-thumb:hover { background: #a1a1a1; }
  `}</style>
);

// ─── DATA ────────────────────────────────────────────────────────────────────

const NAV_MEGA = [
  {
    label: 'Services',
    page: 'services',
    mega: {
      sections: [
        {
          heading: 'Cloud & Infrastructure',
          items: [
            { icon: '⬡', title: 'Cloud Migration', desc: 'Move workloads to AWS, Azure & GCP' },
            { icon: '◻', title: 'Infrastructure Modernization', desc: 'Replace legacy with scalable architecture' },
            { icon: '▷', title: 'FinOps & Cost Optimization', desc: 'Maximize cloud ROI' },
            { icon: '◈', title: 'DevOps & CI/CD', desc: 'Accelerate software delivery pipelines' },
          ],
        },
        {
          heading: 'AI & Data',
          items: [
            { icon: '⬟', title: 'Generative AI Integration', desc: 'Embed LLMs into enterprise workflows' },
            { icon: '○', title: 'Data Platform Engineering', desc: 'Unified lakehouse & pipeline design' },
            { icon: '△', title: 'Predictive Analytics', desc: 'ML models that drive decisions' },
            { icon: '⬢', title: 'MLOps & AI Governance', desc: 'Production-grade model management' },
          ],
        },
        {
          heading: 'Enterprise & Security',
          items: [
            { icon: '◇', title: 'Cybersecurity & Zero Trust', desc: 'Protect your entire digital estate' },
            { icon: '△', title: 'ERP & SAP Implementations', desc: 'SAP S/4HANA, Salesforce, Oracle' },
            { icon: '◈', title: 'Managed Services & NOC', desc: '24/7 operations with SLA guarantees' },
            { icon: '▷', title: 'Automation & RPA', desc: 'Eliminate manual effort at scale' },
          ],
        },
      ],
      featured: {
        label: 'New',
        title: 'Techsolist AI Labs',
        desc: 'Our GenAI practice helping enterprises move from pilot to production.',
        cta: 'Learn more →',
      },
    },
  },
  {
    label: 'Industries',
    page: null,
    mega: {
      sections: [
        {
          heading: 'Financial Services',
          items: [
            { icon: '◈', title: 'Banking & Payments', desc: 'Core banking modernization' },
            { icon: '⬡', title: 'Capital Markets', desc: 'Trading platforms & risk systems' },
            { icon: '◇', title: 'Insurance', desc: 'Claims automation & underwriting AI' },
          ],
        },
        {
          heading: 'Public Sector & Health',
          items: [
            { icon: '△', title: 'Government & Defence', desc: 'Secure digital government platforms' },
            { icon: '○', title: 'Healthcare & Life Sciences', desc: 'Patient-centric digital health' },
            { icon: '⬟', title: 'Education', desc: 'EdTech and campus modernization' },
          ],
        },
        {
          heading: 'Commerce & Energy',
          items: [
            { icon: '▷', title: 'Retail & eCommerce', desc: 'Omnichannel commerce platforms' },
            { icon: '⬢', title: 'Energy & Utilities', desc: 'Smart grid and sustainability tech' },
            { icon: '◻', title: 'Manufacturing', desc: 'Industry 4.0 and IoT integration' },
          ],
        },
      ],
      featured: {
        label: 'Report',
        title: 'Industry Outlook 2025',
        desc: 'Which sectors will see the highest technology ROI this year?',
        cta: 'Download report →',
      },
    },
  },
  {
    label: 'Insights',
    page: null,
    mega: {
      sections: [
        {
          heading: 'Content',
          items: [
            { icon: '◈', title: 'Research Reports', desc: 'Data-driven industry analysis' },
            { icon: '△', title: 'Case Studies', desc: 'Real transformation outcomes' },
            { icon: '○', title: 'Thought Leadership', desc: 'Expert perspectives from our team' },
            { icon: '▷', title: 'Webinars & Events', desc: 'Live and on-demand sessions' },
          ],
        },
        {
          heading: 'Topics',
          items: [
            { icon: '⬟', title: 'Artificial Intelligence', desc: 'GenAI, ML, and automation' },
            { icon: '⬡', title: 'Cloud Strategy', desc: 'Multi-cloud and hybrid decisions' },
            { icon: '◇', title: 'Cybersecurity Trends', desc: 'Threat landscape and defences' },
            { icon: '⬢', title: 'Digital Transformation', desc: 'How leaders make change stick' },
          ],
        },
      ],
      featured: {
        label: 'Featured',
        title: 'Tech Vision 2025',
        desc: "Our annual flagship report on what's reshaping enterprise technology.",
        cta: 'Read now →',
      },
    },
  },
  { label: 'About', page: 'about', mega: null },
  { label: 'Careers', page: 'careers', mega: null },
];

const ALL_SERVICES = [
  {
    icon: '◈',
    title: 'Digital Transformation',
    short: 'End-to-end business modernization',
    desc: 'We partner with enterprises to reimagine processes, culture, and technology from the ground up—delivering measurable outcomes and lasting competitive advantage.',
    tags: ['Strategy', 'Change Management', 'Agile', 'BPR'],
    details: [
      {
        heading: 'Business Process Reengineering',
        body: 'We audit and redesign your core workflows using lean methodologies and automation-first thinking, eliminating bottlenecks and compressing cycle times by up to 60%.',
      },
      {
        heading: 'Change Management & Adoption',
        body: 'Transformation only succeeds when people embrace it. Our certified change practitioners design communication plans, training programs, and adoption metrics to ensure your workforce moves with the initiative, not against it.',
      },
      {
        heading: 'Agile at Scale',
        body: 'We implement SAFe, LeSS, and custom scaling frameworks that move your enterprise from annual waterfall plans to quarterly value delivery cycles—without the chaos.',
      },
      {
        heading: 'Digital Operating Model Design',
        body: 'We help leadership define the right operating model for a digital-first era: governance structures, center-of-excellence setup, build-buy-partner decisions, and the metrics that matter.',
      },
    ],
    outcomes: [
      'Up to 60% reduction in process cycle time',
      'Average 35% improvement in employee productivity',
      '92% adoption rate within 90 days of go-live',
      'ROI realized within 12–18 months',
    ],
  },
  {
    icon: '⬡',
    title: 'Cloud & Infrastructure',
    short: 'Scalable multi-cloud solutions',
    desc: 'Migrate, modernize, and optimize across AWS, Azure, and GCP. We build resilient, cost-efficient cloud platforms tailored for enterprise workloads.',
    tags: ['AWS', 'Azure', 'GCP', 'FinOps', 'DevOps'],
    details: [
      {
        heading: 'Cloud Migration & Lift-Shift',
        body: 'We assess, plan, and execute migrations of 6 Rs (rehost, replatform, refactor, repurchase, retire, retain) across AWS, Azure, and GCP, using automated tooling to reduce migration risk and timelines by up to 40%.',
      },
      {
        heading: 'Infrastructure as Code (IaC)',
        body: 'Every environment we build is codified using Terraform, Pulumi, or AWS CDK—ensuring consistency, repeatability, and version control from day one.',
      },
      {
        heading: 'FinOps & Cloud Cost Governance',
        body: 'Our dedicated FinOps team implements tagging strategies, reserved instance planning, spot-instance automation, and real-time dashboards that consistently reduce cloud spend by 25–40%.',
      },
      {
        heading: 'Hybrid & Edge Architecture',
        body: 'For organizations that cannot fully migrate, we architect hybrid environments with seamless connectivity, consistent security posture, and unified management across on-premise and cloud.',
      },
    ],
    outcomes: [
      'Average 30% reduction in cloud costs within 6 months',
      '99.99% uptime SLA on managed cloud environments',
      'Migration timelines reduced by up to 40%',
      'Zero unplanned downtime during migrations for 97% of clients',
    ],
  },
  {
    icon: '⬟',
    title: 'AI & Data Analytics',
    short: 'Intelligence-driven decisions',
    desc: 'From GenAI integration to real-time analytics pipelines, we help organizations harness data to create predictive, automated, and insight-rich operations.',
    tags: ['GenAI', 'MLOps', 'Data Platform', 'BI'],
    details: [
      {
        heading: 'Generative AI Integration',
        body: 'We embed LLMs (GPT-4, Claude, Gemini, Llama) into enterprise workflows—customer service automation, document intelligence, code generation, and knowledge management—using retrieval-augmented generation (RAG) for accuracy and compliance.',
      },
      {
        heading: 'Data Lakehouse & Pipeline Engineering',
        body: 'We design and build unified data platforms on Databricks, Snowflake, or BigQuery, with streaming ingestion (Kafka, Kinesis), orchestration (Airflow, Dagster), and governance (Unity Catalog, Apache Atlas).',
      },
      {
        heading: 'Machine Learning & Predictive Analytics',
        body: 'Our data scientists and ML engineers build, validate, and deploy production-grade models for demand forecasting, fraud detection, churn prediction, and personalization at scale.',
      },
      {
        heading: 'MLOps & AI Governance',
        body: 'We implement end-to-end MLOps pipelines with automated retraining, drift detection, model registries, and explainability tooling to keep your AI responsible, auditable, and production-ready.',
      },
    ],
    outcomes: [
      '40% average reduction in time-to-insight',
      'GenAI deployments in production within 8 weeks',
      'Data platform consolidation saving 35% in tooling costs',
      'Model accuracy improvements of 20–45% vs. baseline',
    ],
  },
  {
    icon: '◇',
    title: 'Cybersecurity',
    short: 'Zero-trust protection',
    desc: 'Security by design. We implement zero-trust architectures, 24/7 SOC operations, and compliance frameworks to safeguard your digital estate.',
    tags: ['Zero Trust', 'SOC', 'Compliance', 'SIEM'],
    details: [
      {
        heading: 'Zero Trust Architecture',
        body: 'We implement identity-centric, least-privilege security models using Microsoft Entra, Okta, and Zscaler—ensuring every user, device, and workload is continuously verified regardless of network location.',
      },
      {
        heading: '24/7 SOC & Threat Detection',
        body: 'Our Security Operations Center combines SIEM (Splunk, Microsoft Sentinel), SOAR automation, and expert analysts to detect, triage, and respond to threats with a median response time under 15 minutes.',
      },
      {
        heading: 'Penetration Testing & Red Teaming',
        body: 'Our CREST-certified ethical hackers simulate advanced persistent threats against your applications, infrastructure, and human layer—providing remediation roadmaps, not just findings reports.',
      },
      {
        heading: 'Compliance & Risk Management',
        body: 'We accelerate compliance with ISO 27001, SOC 2, GDPR, HIPAA, PCI-DSS, and NIST frameworks through automated evidence collection, policy management platforms, and audit-ready reporting.',
      },
    ],
    outcomes: [
      'Mean time to detect (MTTD) reduced to under 15 minutes',
      '100% of clients passed compliance audits first attempt',
      'Attack surface reduced by average 68% post-engagement',
      'Zero successful ransomware incidents across managed client base in 2024',
    ],
  },
  {
    icon: '△',
    title: 'Enterprise Software',
    short: 'Custom platforms & ERP',
    desc: 'Custom platforms, system integration, and ERP implementations that connect your enterprise and unlock operational excellence at every layer.',
    tags: ['SAP', 'Salesforce', 'Custom Dev', 'API'],
    details: [
      {
        heading: 'SAP S/4HANA Implementation & Migration',
        body: 'We manage end-to-end SAP transformations—greenfield implementations, system conversions, and selective data transitions—leveraging our SAP Platinum Partner status and 200+ certified consultants.',
      },
      {
        heading: 'Salesforce CRM & Platform',
        body: 'From Sales Cloud and Service Cloud to custom AppExchange solutions, we design, develop, and optimize Salesforce environments that drive revenue growth and customer retention.',
      },
      {
        heading: 'Custom Enterprise Application Development',
        body: 'For requirements that off-the-shelf cannot meet, our engineers build scalable, maintainable applications using modern stacks (React, Node.js, .NET, Java, Go) with full DevSecOps integration.',
      },
      {
        heading: 'API Strategy & Integration',
        body: 'We architect and implement enterprise integration platforms using MuleSoft, Azure Integration Services, and Kong Gateway—creating the digital backbone that connects your application ecosystem.',
      },
    ],
    outcomes: [
      'SAP implementations delivered on average 2 months ahead of schedule',
      'API-led integration reducing integration costs by 45%',
      'Custom platforms handling 500M+ daily transactions',
      '99.95% uptime SLA on all managed enterprise applications',
    ],
  },
  {
    icon: '○',
    title: 'Technology Strategy',
    short: 'CTO advisory & roadmapping',
    desc: 'Our strategists help boards and CTOs align technology investment with business ambition, navigating M&A, legacy debt, and emerging disruption.',
    tags: ['CTO Advisory', 'M&A Tech', 'Roadmapping'],
    details: [
      {
        heading: 'Technology Roadmapping',
        body: 'We facilitate executive workshops and produce 3-year technology roadmaps that sequence investments for maximum business impact, balancing innovation ambition with technical debt reduction and operational stability.',
      },
      {
        heading: 'M&A Technology Due Diligence',
        body: 'Our specialists conduct rapid, comprehensive technology assessments of acquisition targets—evaluating architecture quality, tech debt, team capability, and integration complexity—typically within a 2–4 week timeline.',
      },
      {
        heading: 'Build vs. Buy vs. Partner Analysis',
        body: 'We apply structured decision frameworks to help organizations make optimal make-or-buy decisions, drawing on benchmarks across thousands of similar decisions to avoid costly mistakes.',
      },
      {
        heading: 'Board & C-Suite Technology Advisory',
        body: 'Our senior principals serve as trusted advisors to boards and CXOs, translating complex technology dynamics into business language and providing the independent perspective needed for high-stakes decisions.',
      },
    ],
    outcomes: [
      'Technology strategy engagements completed in 6–8 weeks',
      'M&A due diligence reducing post-acquisition surprises by 80%',
      'Roadmap-guided investments showing 3.2x better ROI vs. peer benchmarks',
      'CTO advisory retainers averaging 18-month duration',
    ],
  },
  {
    icon: '▷',
    title: 'Managed Services',
    short: '24/7 operations & support',
    desc: 'Offload operational complexity to our expert teams. We manage your infrastructure, applications, and security with guaranteed SLAs.',
    tags: ['NOC', 'SLA', 'ITIL', 'Monitoring'],
    details: [
      {
        heading: 'Network Operations Center (NOC)',
        body: 'Our 24/7/365 NOC provides proactive monitoring, incident management, and resolution for your entire infrastructure estate—with tiered SLAs, escalation paths, and full ITIL-compliant processes.',
      },
      {
        heading: 'Application Managed Services',
        body: 'We take ownership of application performance, availability, and evolution post-implementation—handling bug fixes, enhancements, security patching, and continuous optimization so your team can focus on innovation.',
      },
      {
        heading: 'Database Administration',
        body: 'Expert DBAs manage your relational and NoSQL databases (Oracle, SQL Server, PostgreSQL, MongoDB) with proactive performance tuning, backup verification, and upgrade management.',
      },
      {
        heading: 'Observability & AIOps',
        body: 'We implement unified observability stacks (Datadog, Dynatrace, New Relic) with AI-powered anomaly detection, automated runbook execution, and predictive capacity management to prevent issues before they impact users.',
      },
    ],
    outcomes: [
      '99.99% uptime achieved across managed client infrastructure in 2024',
      'Mean time to resolve (MTTR) 68% faster than industry average',
      'Incident volume reduced 45% within 90 days via AIOps',
      'Average client IT cost reduction of 28% through managed services model',
    ],
  },
  {
    icon: '◻',
    title: 'Product Engineering',
    short: 'Build-to-scale software delivery',
    desc: 'Full-cycle product engineering from UX research through launch and iteration—built by cross-functional teams using modern engineering practices.',
    tags: ['UX', 'Full-Stack', 'QA', 'CI/CD'],
    details: [
      {
        heading: 'UX Research & Product Design',
        body: 'Our UX researchers conduct user interviews, contextual inquiry, and usability testing to inform product decisions. Designers translate insights into high-fidelity prototypes and design systems that scale across your product portfolio.',
      },
      {
        heading: 'Full-Stack Engineering',
        body: 'Cross-functional squads deliver production-grade software using React/Next.js, React Native, Node.js, Python, and Go—with test-driven development, code review culture, and engineering excellence baked in from day one.',
      },
      {
        heading: 'Platform & API Engineering',
        body: 'We build the foundational platforms—API gateways, microservices architectures, event-driven systems—that power your product ecosystem and enable teams to ship faster with fewer dependencies.',
      },
      {
        heading: 'Quality Assurance & Test Automation',
        body: 'Our QA engineers implement comprehensive automated testing strategies (unit, integration, E2E, performance, security) achieving 80%+ coverage and enabling confident deployment pipelines.',
      },
    ],
    outcomes: [
      'Product delivery 40% faster than client in-house benchmarks',
      '80%+ automated test coverage on all engagements',
      'CSAT scores averaging 4.7/5 across product teams',
      'Zero critical defects in production for 88% of launches',
    ],
  },
  {
    icon: '⬢',
    title: 'Automation & RPA',
    short: 'Intelligent process automation',
    desc: 'We identify, design, and deploy RPA and intelligent automation solutions that eliminate manual effort and accelerate throughput across departments.',
    tags: ['RPA', 'Workflow', 'Integration', 'AI'],
    details: [
      {
        heading: 'RPA Discovery & Process Mining',
        body: 'We use process mining tools (Celonis, UiPath Process Mining) to identify the highest-ROI automation candidates from your operational data, prioritizing by volume, error rate, and strategic value—not gut feel.',
      },
      {
        heading: 'UiPath & Power Automate Implementation',
        body: 'Our certified RPA developers build robust, maintainable automation workflows using UiPath, Automation Anywhere, and Microsoft Power Automate—with full exception handling, audit logging, and monitoring dashboards.',
      },
      {
        heading: 'Intelligent Document Processing',
        body: 'We deploy AI-powered document capture and extraction solutions (Azure Form Recognizer, Google Document AI) that handle invoices, contracts, and forms with 99%+ extraction accuracy—replacing hours of manual data entry.',
      },
      {
        heading: 'Hyperautomation & Orchestration',
        body: 'Beyond individual bots, we architect hyperautomation ecosystems that combine RPA, AI, APIs, and low-code platforms into end-to-end automated value streams spanning multiple departments and systems.',
      },
    ],
    outcomes: [
      'Average 12x ROI on RPA investments within 18 months',
      '85% reduction in manual processing time',
      '99%+ extraction accuracy in document processing',
      'Automation portfolio typically covering 40–60 processes within year one',
    ],
  },
];

const STATS = [
  { value: '50+', label: 'Projects delivered' },
  { value: '30+', label: 'Enterprise clients served' },
  { value: '15+', label: 'Technology partnerships' },
  { value: '100%', label: 'On-time delivery commitment' },
];

const CASE_STUDIES = [
  {
    industry: 'Financial Services',
    title: 'Global bank cuts operational costs by 34% through AI automation',
    tags: ['AI', 'Cloud', 'Process Automation'],
  },
  {
    industry: 'Healthcare',
    title: 'National health system reduces patient wait times by 40% with predictive analytics',
    tags: ['Data Analytics', 'Digital Health'],
  },
  {
    industry: 'Retail',
    title: 'Fortune 500 retailer unifies 28 markets on a single commerce platform',
    tags: ['Platform', 'Cloud Migration'],
  },
];

const INSIGHTS = [
  {
    category: 'Research',
    title: 'Technology Vision 2025: The Intelligent Enterprise',
    date: 'May 2025',
    readTime: '12 min',
  },
  {
    category: 'Thought Leadership',
    title: 'Generative AI at Scale: From Pilot to Production',
    date: 'April 2025',
    readTime: '8 min',
  },
  {
    category: 'Case Study',
    title: 'How Meridian Energy Achieved Carbon Neutrality Through Technology',
    date: 'March 2025',
    readTime: '6 min',
  },
];

const TEAM = [
  { name: 'Sarah Chen', role: 'Chief Executive Officer', exp: 'Former McKinsey Partner, 22 years in tech consulting' },
  {
    name: 'Marcus Reid',
    role: 'Chief Technology Officer',
    exp: 'Ex-Google Engineering Director, 18 years building enterprise platforms',
  },
  {
    name: 'Priya Nair',
    role: 'Chief Operating Officer',
    exp: 'Stanford MBA, led 400+ digital transformation engagements',
  },
  {
    name: 'James Okafor',
    role: 'Chief Strategy Officer',
    exp: 'Former IBM Global Strategist, specialist in M&A technology integration',
  },
];

const VALUES = [
  {
    title: 'Client Obsession',
    desc: 'Every engagement starts and ends with one question: what does success look like for our client?',
  },
  {
    title: 'Intellectual Honesty',
    desc: 'We tell clients what they need to hear, not what they want to hear. Trust is built on truth.',
  },
  {
    title: 'Relentless Innovation',
    desc: "We invest 8% of revenue in R&D annually to ensure our clients always have access to what's next.",
  },
  {
    title: 'Inclusive Excellence',
    desc: 'Our diversity is our competitive advantage. 67 nationalities, one shared standard of excellence.',
  },
];

const OPEN_ROLES = [
  { title: 'Senior Cloud Architect', location: 'New York / Remote', dept: 'Cloud & Infrastructure', type: 'Full-time' },
  { title: 'AI/ML Engineer', location: 'London / Hybrid', dept: 'AI & Data Analytics', type: 'Full-time' },
  { title: 'SAP Functional Consultant', location: 'Dubai / On-site', dept: 'Enterprise Software', type: 'Full-time' },
  { title: 'Cybersecurity Analyst', location: 'Singapore / Hybrid', dept: 'Cybersecurity', type: 'Full-time' },
  {
    title: 'Product Manager — Digital',
    location: 'San Francisco / Remote',
    dept: 'Product Engineering',
    type: 'Full-time',
  },
  { title: 'Technology Strategy Consultant', location: 'Multiple Locations', dept: 'Strategy', type: 'Full-time' },
  { title: 'DevOps Engineer', location: 'Remote', dept: 'Cloud & Infrastructure', type: 'Full-time' },
  { title: 'UX Designer', location: 'Berlin / Hybrid', dept: 'Product Engineering', type: 'Full-time' },
];

const OFFICES = [
  { city: 'New York', address: 'One World Trade Center, Suite 8500', phone: '+0 000 000 0000' },
  { city: 'London', address: '22 Bishopsgate, 40th Floor, EC2N 4BQ', phone: '+00 00 0000 0000' },
  { city: 'Dubai', address: 'DIFC Gate Building, Level 14', phone: '+971 0 000 0000' },
  { city: 'Singapore', address: 'Marina Bay Financial Centre, Tower 3', phone: '+00 0000 0000' },
];

const COUNTRIES = [
  { code: '🌐', label: 'Global (EN)', name: 'Global (EN' },
  { code: '🇺🇸', label: 'United States', name: 'USA' },
  { code: '🇬🇧', label: 'United Kingdom', name: 'UK' },
  { code: '🇦🇪', label: 'UAE', name: 'UAE' },
  { code: '🇸🇬', label: 'Singapore', name: 'SG' },
  { code: '🇩🇪', label: 'Germany', name: 'DE' },
  { code: '🇦🇺', label: 'Australia', name: 'AU' },
  { code: '🇮🇳', label: 'India', name: 'IN' },
  { code: '🇨🇦', label: 'Canada', name: 'CA' },
  { code: '🇫🇷', label: 'France', name: 'FR' },
  { code: '🇯🇵', label: 'Japan', name: 'JP' },
];

// ─── HOOKS ───────────────────────────────────────────────────────────────────

function useWindowWidth() {
  const [width, setWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return width;
}

// ─── COUNTRY DROPDOWN ────────────────────────────────────────────────────────

function CountryDropdown() {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(COUNTRIES[0]);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          background: 'none',
          border: '1.5px solid #e8e8e8',
          padding: '7px 12px',
          cursor: 'pointer',
          fontFamily: "'Poppins', sans-serif",
          fontSize: '12.5px',
          fontWeight: '500',
          color: '#374151',
          transition: 'border-color 0.15s',
        }}
      >
        <span style={{ fontSize: '14px' }}>{selected.code}</span>
        <span style={{ maxWidth: '80px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selected.name}
        </span>
        <svg
          width="10"
          height="10"
          viewBox="0 0 12 12"
          fill="none"
          style={{ transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'rotate(0)', flexShrink: 0 }}
        >
          <path d="M2 4l4 4 4-4" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            background: '#fff',
            border: '1px solid #e8e8e8',
            boxShadow: '0 16px 40px rgba(0,0,0,0.10)',
            minWidth: '180px',
            zIndex: 2000,
            maxHeight: '320px',
            overflowY: 'auto',
          }}
        >
          {COUNTRIES.map((c, i) => (
            <div
              key={i}
              onClick={() => {
                setSelected(c);
                setOpen(false);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '10px 16px',
                cursor: 'pointer',
                background: selected.label === c.label ? '#f3f3f3' : 'transparent',
                borderBottom: i < COUNTRIES.length - 1 ? '1px solid #f7f7f7' : 'none',
              }}
              onMouseEnter={(e) => {
                if (selected.label !== c.label) e.currentTarget.style.background = '#f9f9f9';
              }}
              onMouseLeave={(e) => {
                if (selected.label !== c.label) e.currentTarget.style.background = 'transparent';
              }}
            >
              <span style={{ fontSize: '15px' }}>{c.code}</span>
              <span
                style={{
                  fontSize: '13px',
                  fontWeight: selected.label === c.label ? '600' : '400',
                  color: '#0a0a0a',
                  fontFamily: "'Poppins', sans-serif",
                }}
              >
                {c.label}
              </span>
              {selected.label === c.label && (
                <span style={{ marginLeft: 'auto', fontSize: '11px', color: '#1a56db' }}>✓</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── MOBILE DRAWER ───────────────────────────────────────────────────────────

function MobileDrawer({ open, onClose, navigate }) {
  const [expandedItem, setExpandedItem] = useState(null);

  const handleNavClick = (page) => {
    if (page) {
      navigate(page);
      onClose();
    }
  };

  return (
    <>
      {/* Overlay */}
      {open && (
        <div
          onClick={onClose}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            zIndex: 1998,
            backdropFilter: 'blur(2px)',
          }}
        />
      )}
      {/* Drawer */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: '85%',
          maxWidth: '360px',
          background: '#fff',
          zIndex: 1999,
          overflowY: 'auto',
          transform: open ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.3s cubic-bezier(0.4,0,0.2,1)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Drawer header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '20px 24px',
            borderBottom: '1px solid #e8e8e8',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <svg width="18" height="18" viewBox="0 0 30 30" fill="none">
              <rect width="13" height="13" fill="#1a56db" />
              <rect x="17" width="13" height="13" fill="#0a0a0a" />
              <rect y="17" width="13" height="13" fill="#0a0a0a" />
              <rect x="17" y="17" width="13" height="13" fill="#1a56db" />
            </svg>
            <span style={{ fontSize: '15px', fontWeight: '800', letterSpacing: '-0.5px', color: '#0a0a0a' }}>
              tech<span style={{ color: '#1a56db' }}>solist</span>
            </span>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              width: '36px',
              height: '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#6b7280',
              fontSize: '20px',
              borderRadius: '4px',
            }}
          >
            ✕
          </button>
        </div>

        {/* Nav items */}
        <nav style={{ flex: 1, padding: '8px 0' }}>
          {NAV_MEGA.map((item) => (
            <div key={item.label}>
              <div
                onClick={() =>
                  item.mega
                    ? setExpandedItem(expandedItem === item.label ? null : item.label)
                    : handleNavClick(item.page)
                }
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '14px 24px',
                  cursor: 'pointer',
                  borderBottom: '1px solid #f5f5f5',
                  background: expandedItem === item.label ? '#f9f9f9' : 'transparent',
                }}
              >
                <span style={{ fontSize: '15px', fontWeight: '600', color: '#0a0a0a' }}>{item.label}</span>
                {item.mega && (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 12 12"
                    fill="none"
                    style={{
                      transition: 'transform 0.2s',
                      transform: expandedItem === item.label ? 'rotate(180deg)' : 'rotate(0)',
                    }}
                  >
                    <path
                      d="M2 4l4 4 4-4"
                      stroke="#6b7280"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </div>
              {item.mega && expandedItem === item.label && (
                <div style={{ background: '#f9f9f9', padding: '12px 24px 16px' }}>
                  {item.mega.sections.map((section, si) => (
                    <div key={si} style={{ marginBottom: '16px' }}>
                      <div
                        style={{
                          fontSize: '10px',
                          fontWeight: '700',
                          letterSpacing: '1.5px',
                          textTransform: 'uppercase',
                          color: '#9ca3af',
                          marginBottom: '8px',
                        }}
                      >
                        {section.heading}
                      </div>
                      {section.items.map((subItem, ii) => (
                        <div
                          key={ii}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '8px 0',
                            cursor: 'pointer',
                          }}
                          onClick={() => {
                            if (item.page) {
                              navigate(item.page);
                              onClose();
                            }
                          }}
                        >
                          <span style={{ color: '#1a56db', fontSize: '13px', width: '16px' }}>{subItem.icon}</span>
                          <span style={{ fontSize: '13px', fontWeight: '500', color: '#0a0a0a' }}>{subItem.title}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* CTA */}
        <div
          style={{
            padding: '20px 24px',
            borderTop: '1px solid #e8e8e8',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
          }}
        >
          <button
            className="btn-primary"
            style={{ width: '100%', textAlign: 'center' }}
            onClick={() => {
              navigate('contact');
              onClose();
            }}
          >
            Get in Touch
          </button>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <CountryDropdown />
          </div>
        </div>
      </div>
    </>
  );
}

// ─── MEGA DROPDOWN (DESKTOP) ──────────────────────────────────────────────────

function MegaDropdown({ mega, visible }) {
  if (!mega || !visible) return null;
  return (
    <div
      style={{
        position: 'fixed',
        top: '68px',
        left: 0,
        right: 0,
        background: '#fff',
        borderTop: '1px solid #e8e8e8',
        borderBottom: '1px solid #e8e8e8',
        boxShadow: '0 24px 60px rgba(0,0,0,0.10)',
        zIndex: 999,
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(-8px)',
        transition: 'opacity 0.18s ease, transform 0.18s ease',
        pointerEvents: visible ? 'all' : 'none',
      }}
    >
      <div className="container" style={{ padding: '40px 40px', display: 'flex', gap: '0' }}>
        <div
          style={{
            flex: 1,
            display: 'grid',
            gridTemplateColumns: `repeat(${mega.sections.length}, 1fr)`,
            gap: '0',
            borderRight: '1px solid #f0f0f0',
          }}
        >
          {mega.sections.map((section, si) => (
            <div
              key={si}
              style={{
                padding: '0 36px 0 0',
                marginRight: '36px',
                borderRight: si < mega.sections.length - 1 ? '1px solid #f0f0f0' : 'none',
              }}
            >
              <div
                style={{
                  fontSize: '10px',
                  fontWeight: '700',
                  letterSpacing: '2px',
                  textTransform: 'uppercase',
                  color: '#9ca3af',
                  marginBottom: '20px',
                  paddingBottom: '12px',
                  borderBottom: '1px solid #f3f3f3',
                }}
              >
                {section.heading}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {section.items.map((item, ii) => (
                  <div
                    key={ii}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px',
                      padding: '10px 12px',
                      cursor: 'pointer',
                      transition: 'background 0.12s',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = '#f9f9f9')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <div style={{ fontSize: '14px', color: '#1a56db', marginTop: '2px', flexShrink: 0, width: '18px' }}>
                      {item.icon}
                    </div>
                    <div>
                      <div style={{ fontSize: '13.5px', fontWeight: '600', color: '#0a0a0a', lineHeight: '1.3' }}>
                        {item.title}
                      </div>
                      <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px', lineHeight: '1.4' }}>
                        {item.desc}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        {mega.featured && (
          <div style={{ width: '260px', paddingLeft: '36px', flexShrink: 0 }}>
            <div style={{ background: '#f9f9f9', border: '1px solid #f0f0f0', padding: '28px 24px' }}>
              <div
                style={{
                  fontSize: '10px',
                  fontWeight: '700',
                  letterSpacing: '1.5px',
                  textTransform: 'uppercase',
                  color: '#1a56db',
                  marginBottom: '12px',
                }}
              >
                {mega.featured.label}
              </div>
              <div
                style={{
                  fontSize: '15px',
                  fontWeight: '700',
                  color: '#0a0a0a',
                  lineHeight: '1.35',
                  marginBottom: '10px',
                }}
              >
                {mega.featured.title}
              </div>
              <div style={{ fontSize: '12.5px', color: '#9ca3af', lineHeight: '1.6', marginBottom: '20px' }}>
                {mega.featured.desc}
              </div>
              <div style={{ fontSize: '12.5px', fontWeight: '600', color: '#1a56db', cursor: 'pointer' }}>
                {mega.featured.cta}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── HEADER ──────────────────────────────────────────────────────────────────

function Header({ scrolled, currentPage, navigate }) {
  const [activeMenu, setActiveMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const closeTimer = useRef(null);
  const width = useWindowWidth();
  const isMobile = width < 900;

  const handleEnter = (label) => {
    clearTimeout(closeTimer.current);
    setActiveMenu(label);
  };
  const handleLeave = () => {
    closeTimer.current = setTimeout(() => setActiveMenu(null), 180);
  };
  const activeItem = NAV_MEGA.find((n) => n.label === activeMenu);

  return (
    <>
      <header
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          background: scrolled || activeMenu ? 'rgba(255,255,255,0.99)' : 'rgba(255,255,255,0.96)',
          backdropFilter: 'blur(16px)',
          borderBottom: scrolled || activeMenu ? '1px solid #e8e8e8' : '1px solid transparent',
          transition: 'all 0.25s ease',
          boxShadow: scrolled ? '0 1px 24px rgba(0,0,0,0.06)' : 'none',
        }}
      >
        <div
          className="container"
          style={{ height: '68px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          {/* Logo */}
          <div
            onClick={() => navigate('home')}
            style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', flexShrink: 0 }}
          >
            <svg width="20" height="20" viewBox="0 0 30 30" fill="none">
              <rect width="13" height="13" fill="#1a56db" />
              <rect x="17" width="13" height="13" fill="#0a0a0a" />
              <rect y="17" width="13" height="13" fill="#0a0a0a" />
              <rect x="17" y="17" width="13" height="13" fill="#1a56db" />
            </svg>
            <img src="/techsolistlogo.png" style={{ width: '120px', filter: 'saturate(0.5)' }} />
            {/* <span style={{ fontSize: '17px', fontWeight: '800', letterSpacing: '-0.8px', color: '#0a0a0a' }}>
              tech<span style={{ color: '#1a56db' }}>solist</span>
            </span> */}
          </div>

          {/* Desktop Nav */}
          {!isMobile && (
            <nav style={{ display: 'flex', alignItems: 'stretch', height: '68px' }} onMouseLeave={handleLeave}>
              {NAV_MEGA.map((link) => (
                <div
                  key={link.label}
                  onMouseEnter={() => (link.mega ? handleEnter(link.label) : setActiveMenu(null))}
                  style={{ display: 'flex', alignItems: 'center' }}
                >
                  <button
                    onClick={() => {
                      if (link.page) navigate(link.page);
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: '0 18px',
                      height: '68px',
                      fontSize: '13.5px',
                      fontWeight: currentPage === link.page || activeMenu === link.label ? '600' : '500',
                      color: currentPage === link.page || activeMenu === link.label ? '#0a0a0a' : '#6b7280',
                      cursor: 'pointer',
                      transition: 'color 0.15s',
                      borderBottom: currentPage === link.page ? '2px solid #0a0a0a' : '2px solid transparent',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                      fontFamily: "'Poppins', sans-serif",
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {link.label}
                    {link.mega && (
                      <svg
                        width="11"
                        height="11"
                        viewBox="0 0 12 12"
                        fill="none"
                        style={{
                          transition: 'transform 0.2s',
                          transform: activeMenu === link.label ? 'rotate(180deg)' : 'rotate(0)',
                        }}
                      >
                        <path
                          d="M2 4l4 4 4-4"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </button>
                </div>
              ))}
            </nav>
          )}

          {/* Right side */}
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexShrink: 0 }}>
            {!isMobile && <CountryDropdown />}
            {/* {!isMobile && (
              <button
                className="btn-primary"
                onClick={() => navigate('contact')}
                style={{ padding: '10px 22px', fontSize: '12.5px' }}
              >
                Get in Touch
              </button>
            )} */}
            {/* Hamburger */}
            {isMobile && (
              <button
                onClick={() => setMobileOpen(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '5px',
                  padding: '8px',
                  width: '40px',
                  height: '40px',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <span
                  style={{ display: 'block', width: '22px', height: '2px', background: '#0a0a0a', borderRadius: '2px' }}
                />
                <span
                  style={{ display: 'block', width: '22px', height: '2px', background: '#0a0a0a', borderRadius: '2px' }}
                />
                <span
                  style={{
                    display: 'block',
                    width: '14px',
                    height: '2px',
                    background: '#0a0a0a',
                    borderRadius: '2px',
                    alignSelf: 'flex-start',
                  }}
                />
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Mega dropdown */}
      {!isMobile && (
        <div onMouseEnter={() => activeMenu && handleEnter(activeMenu)} onMouseLeave={handleLeave}>
          <MegaDropdown mega={activeItem?.mega} visible={!!activeMenu} />
        </div>
      )}

      {/* Mobile Drawer */}
      <MobileDrawer open={mobileOpen} onClose={() => setMobileOpen(false)} navigate={navigate} />
    </>
  );
}

// ─── SERVICE CARD ─────────────────────────────────────────────────────────────

function ServiceCard({ s, i }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: hov ? '#0a0a0a' : '#fff',
        padding: '44px 40px',
        cursor: 'pointer',
        transition: 'background 0.22s ease',
      }}
    >
      <div
        style={{
          fontSize: '24px',
          color: hov ? 'rgba(255,255,255,0.3)' : '#1a56db',
          marginBottom: '18px',
          transition: 'color 0.22s',
        }}
      >
        {s.icon}
      </div>
      <h3
        style={{
          fontSize: '20px',
          fontWeight: '600',
          color: hov ? '#fff' : '#0a0a0a',
          marginBottom: '10px',
          lineHeight: '1.3',
          transition: 'color 0.22s',
        }}
      >
        {s.title}
      </h3>
      <p
        style={{
          fontSize: '13.5px',
          lineHeight: '1.7',
          color: hov ? 'rgba(255,255,255,0.6)' : '#9ca3af',
          marginBottom: '20px',
          transition: 'color 0.22s',
        }}
      >
        {s.short}
      </p>
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
        {s.tags.slice(0, 3).map((tag, j) => (
          <span
            key={j}
            style={{
              fontSize: '10px',
              fontWeight: '600',
              padding: '3px 9px',
              background: hov ? 'rgba(255,255,255,0.1)' : '#f3f3f3',
              color: hov ? 'rgba(255,255,255,0.7)' : '#6b7280',
              letterSpacing: '0.3px',
              transition: 'all 0.22s',
            }}
          >
            {tag}
          </span>
        ))}
      </div>
    </div>
  );
}

// ─── CTA BANNER ───────────────────────────────────────────────────────────────

function CTABanner({ navigate }) {
  return (
    <section style={{ background: '#0a0a0a', padding: '100px 0', position: 'relative', overflow: 'hidden' }}>
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%,-50%)',
          width: '800px',
          height: '800px',
          background: 'radial-gradient(circle, rgba(26,86,219,0.12) 0%, transparent 65%)',
          pointerEvents: 'none',
        }}
      />
      <div className="container" style={{ textAlign: 'center', position: 'relative' }}>
        <h2
          style={{
            fontSize: 'clamp(28px, 4.5vw, 56px)',
            fontWeight: '800',
            color: '#fff',
            letterSpacing: '-1.5px',
            lineHeight: '1.1',
            marginBottom: '20px',
          }}
        >
          Ready to transform
          <br />
          your business?
        </h2>
        <p
          style={{ fontSize: '15.5px', color: '#6b7280', maxWidth: '500px', margin: '0 auto 40px', lineHeight: '1.75' }}
        >
          Talk to our experts about how Techsolist can help you navigate complexity and accelerate growth.
        </p>
        <div style={{ display: 'flex', gap: '14px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            style={{
              background: '#fff',
              border: 'none',
              color: '#0a0a0a',
              fontFamily: "'Poppins', sans-serif",
              fontSize: '13px',
              fontWeight: '700',
              padding: '16px 36px',
              cursor: 'pointer',
              transition: 'all 0.2s',
              letterSpacing: '0.3px',
            }}
            onClick={() => navigate('contact')}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#1a56db';
              e.currentTarget.style.color = '#fff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#fff';
              e.currentTarget.style.color = '#0a0a0a';
            }}
          >
            Schedule a Consultation
          </button>
          <button className="btn-outline-white" onClick={() => navigate('services')}>
            Explore Solutions
          </button>
        </div>
      </div>
    </section>
  );
}

const TechBanner = ({ navigate }) => {
  return (
    <section style={{ width: '100%', overflow: 'hidden' }}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 1400 530"
        style={{ width: '100%', height: 'auto', display: 'block' }}
        aria-label="Technology Services Banner"
      >
        <defs>
          <style>{`
            .banner-bg { fill: #0A0E1A; }
            .grid-line { stroke: #1E2A45; stroke-width: 0.5; fill: none; }
            .circuit-path { fill: none; stroke-width: 1.2; stroke-linecap: round; stroke-linejoin: round; }
            .c1 { stroke: #00D4FF; }
            .c2 { stroke: #7B5EA7; }
            .c3 { stroke: #00FFB3; }
            .c4 { stroke: #FF6B35; }
            .node-dot { stroke-width: 1.5; }
            .glow-text { font-family: 'Segoe UI', 'Helvetica Neue', Arial, sans-serif; fill: #FFFFFF; }
            .sub-text { font-family: 'Segoe UI', 'Helvetica Neue', Arial, sans-serif; fill: #8899BB; }
            .accent-text { font-family: 'Segoe UI', 'Helvetica Neue', Arial, sans-serif; fill: #00D4FF; }
            .tag-text { font-family: 'Segoe UI', Arial, sans-serif; fill: #00D4FF; font-size: 11px; }
            .dash-flow { stroke-dasharray: 6 4; animation: dash-anim 2.5s linear infinite; }
            .dash-flow-slow { stroke-dasharray: 8 6; animation: dash-anim 4s linear infinite; }
            .dash-flow-rev { stroke-dasharray: 6 4; animation: dash-anim-rev 3s linear infinite; }
            .pulse-dot { animation: pulse 2s ease-in-out infinite; }
            .pulse-dot-2 { animation: pulse 2.8s ease-in-out infinite 0.4s; }
            .pulse-dot-3 { animation: pulse 2.2s ease-in-out infinite 0.9s; }
            .pulse-dot-4 { animation: pulse 3s ease-in-out infinite 1.2s; }
            .float-1 { animation: float1 6s ease-in-out infinite; }
            .float-2 { animation: float2 8s ease-in-out infinite 1s; }
            .float-3 { animation: float3 7s ease-in-out infinite 2s; }
            .fade-in-1 { animation: fadein 1s ease-out forwards; opacity: 0; }
            .fade-in-2 { animation: fadein 1s ease-out 0.3s forwards; opacity: 0; }
            .fade-in-3 { animation: fadein 1s ease-out 0.6s forwards; opacity: 0; }
            .scan-line { animation: scan 4s linear infinite; }
            .hex-spin { animation: hexspin 12s linear infinite; transform-box: fill-box; transform-origin: center; }
            .ring-pulse { animation: ringpulse 3s ease-out infinite; }
            .ring-pulse-2 { animation: ringpulse 3s ease-out 1s infinite; }

            @keyframes dash-anim { to { stroke-dashoffset: -40; } }
            @keyframes dash-anim-rev { to { stroke-dashoffset: 40; } }
            @keyframes pulse {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.4; }
            }
            @keyframes float1 {
              0%, 100% { transform: translateY(0px); }
              50% { transform: translateY(-12px); }
            }
            @keyframes float2 {
              0%, 100% { transform: translateY(0px) translateX(0px); }
              50% { transform: translateY(-8px) translateX(6px); }
            }
            @keyframes float3 {
              0%, 100% { transform: translateY(0px); }
              50% { transform: translateY(10px); }
            }
            @keyframes fadein { to { opacity: 1; } }
            @keyframes scan {
              0% { transform: translateY(-100px); opacity: 0; }
              10% { opacity: 0.4; }
              90% { opacity: 0.4; }
              100% { transform: translateY(600px); opacity: 0; }
            }
            @keyframes hexspin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
            @keyframes ringpulse {
              0% { r: 30; opacity: 0.6; }
              100% { r: 80; opacity: 0; }
            }
          `}</style>

          <linearGradient id="bg-grad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#060A14" />
            <stop offset="50%" stopColor="#0A0E1A" />
            <stop offset="100%" stopColor="#06101E" />
          </linearGradient>
          <linearGradient id="title-grad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#FFFFFF" />
            <stop offset="60%" stopColor="#E8F4FF" />
            <stop offset="100%" stopColor="#00D4FF" />
          </linearGradient>
          <linearGradient id="line-grad-h" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#00D4FF" stopOpacity="0" />
            <stop offset="40%" stopColor="#00D4FF" stopOpacity="1" />
            <stop offset="100%" stopColor="#00D4FF" stopOpacity="0.2" />
          </linearGradient>
          <linearGradient id="hex-grad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#7B5EA7" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#00D4FF" stopOpacity="0.1" />
          </linearGradient>
          <filter id="glow-cyan">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id="text-glow">
            <feGaussianBlur stdDeviation="8" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <radialGradient id="center-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#1A2E55" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#0A0E1A" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="accent-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#00D4FF" stopOpacity="0.15" />
            <stop offset="100%" stopColor="#00D4FF" stopOpacity="0" />
          </radialGradient>
          <clipPath id="banner-clip">
            <rect width="1400" height="530" />
          </clipPath>
        </defs>

        <g clipPath="url(#banner-clip)">
          {/* Background */}
          <rect width="1400" height="530" fill="url(#bg-grad)" />

          {/* Subtle grid */}
          <g opacity="0.4">
            {[100, 200, 300, 400, 500, 600, 700, 800, 900, 1000, 1100, 1200, 1300].map((x) => (
              <line key={`v${x}`} className="grid-line" x1={x} y1="0" x2={x} y2="530" />
            ))}
            {[100, 200, 300, 400].map((y) => (
              <line key={`h${y}`} className="grid-line" x1="0" y1={y} x2="1400" y2={y} />
            ))}
          </g>

          {/* Ambient glows */}
          <ellipse cx="700" cy="250" rx="500" ry="280" fill="url(#center-glow)" />
          <ellipse cx="1050" cy="250" rx="280" ry="200" fill="url(#accent-glow)" />

          {/* ── LEFT CIRCUIT LINES ── */}
          <path className="circuit-path c1 dash-flow" d="M 0 180 L 80 180 L 120 140 L 250 140" />
          <path
            className="circuit-path c1 dash-flow-slow"
            d="M 0 320 L 60 320 L 100 360 L 180 360 L 220 320 L 350 320"
          />
          <path className="circuit-path c3" d="M 140 140 L 140 80 L 200 80" strokeWidth="0.8" opacity="0.6" />
          <path className="circuit-path c2 dash-flow" d="M 80 180 L 80 320" />
          <path className="circuit-path c1" d="M 200 80 L 240 80 L 260 60 L 320 60" strokeWidth="0.8" opacity="0.5" />
          <path className="circuit-path c3" d="M 200 80 L 200 140" strokeWidth="0.8" opacity="0.4" />

          {/* Left circuit nodes */}
          <circle
            className="pulse-dot node-dot"
            cx="80"
            cy="180"
            r="4"
            fill="#00D4FF"
            stroke="#00D4FF"
            filter="url(#glow-cyan)"
          />
          <circle
            className="pulse-dot-2 node-dot"
            cx="120"
            cy="140"
            r="3.5"
            fill="#00D4FF"
            stroke="#00D4FF"
            opacity="0.9"
          />
          <circle
            className="pulse-dot-3 node-dot"
            cx="200"
            cy="80"
            r="3"
            fill="#00FFB3"
            stroke="#00FFB3"
            opacity="0.8"
          />
          <circle
            className="pulse-dot-4 node-dot"
            cx="80"
            cy="320"
            r="4"
            fill="#7B5EA7"
            stroke="#7B5EA7"
            opacity="0.9"
            filter="url(#glow-cyan)"
          />
          <circle
            className="pulse-dot node-dot"
            cx="220"
            cy="320"
            r="3"
            fill="#00D4FF"
            stroke="#00D4FF"
            opacity="0.7"
          />
          <rect x="246" y="136" width="8" height="8" fill="none" stroke="#00D4FF" strokeWidth="1" opacity="0.8" />
          <rect x="346" y="316" width="8" height="8" fill="none" stroke="#7B5EA7" strokeWidth="1" opacity="0.7" />

          {/* ── RIGHT: HEXAGON CLUSTER ── */}
          <circle
            className="ring-pulse"
            cx="1050"
            cy="250"
            fill="none"
            stroke="#00D4FF"
            strokeWidth="1"
            opacity="0.5"
          />
          <circle
            className="ring-pulse-2"
            cx="1050"
            cy="250"
            fill="none"
            stroke="#7B5EA7"
            strokeWidth="0.8"
            opacity="0.4"
          />

          <g transform="translate(1050, 250)">
            <g className="hex-spin" opacity="0.25">
              <polygon
                points="0,-90 78,-45 78,45 0,90 -78,45 -78,-45"
                fill="none"
                stroke="#00D4FF"
                strokeWidth="1"
                strokeDasharray="12 6"
              />
            </g>
            <polygon
              points="0,-65 56,-32 56,32 0,65 -56,32 -56,-32"
              fill="url(#hex-grad)"
              stroke="#7B5EA7"
              strokeWidth="1"
              opacity="0.8"
            />
            <polygon
              points="0,-42 36,-21 36,21 0,42 -36,21 -36,-21"
              fill="#0D1525"
              stroke="#00D4FF"
              strokeWidth="1.2"
              opacity="0.9"
            />
            <circle r="22" fill="#0A1830" stroke="#00D4FF" strokeWidth="1.5" />
            <line x1="-12" y1="0" x2="12" y2="0" stroke="#00D4FF" strokeWidth="1.5" strokeLinecap="round" />
            <line x1="0" y1="-12" x2="0" y2="12" stroke="#00D4FF" strokeWidth="1.5" strokeLinecap="round" />
            <circle r="4" fill="#00D4FF" opacity="0.9" filter="url(#glow-cyan)" />
            <circle className="pulse-dot" cx="0" cy="-65" r="4" fill="#00D4FF" filter="url(#glow-cyan)" />
            <circle className="pulse-dot-2" cx="56" cy="-32" r="3.5" fill="#00FFB3" />
            <circle className="pulse-dot-3" cx="56" cy="32" r="3.5" fill="#7B5EA7" />
            <circle className="pulse-dot-4" cx="0" cy="65" r="4" fill="#00D4FF" filter="url(#glow-cyan)" />
            <circle className="pulse-dot" cx="-56" cy="32" r="3.5" fill="#00FFB3" />
            <circle className="pulse-dot-2" cx="-56" cy="-32" r="3.5" fill="#7B5EA7" />
          </g>

          {/* Floating service tags */}
          <g className="float-1" transform="translate(1050, 250)">
            <rect
              x="80"
              y="-110"
              width="90"
              height="34"
              rx="4"
              fill="#0D1830"
              stroke="#00D4FF"
              strokeWidth="0.8"
              opacity="0.9"
            />
            <text x="125" y="-88" textAnchor="middle" className="tag-text" fontSize="10px">
              Cloud Native
            </text>
          </g>
          <g className="float-2" transform="translate(1050, 250)">
            <rect
              x="-170"
              y="-100"
              width="80"
              height="34"
              rx="4"
              fill="#0D1830"
              stroke="#7B5EA7"
              strokeWidth="0.8"
              opacity="0.9"
            />
            <text x="-130" y="-78" textAnchor="middle" className="tag-text" fill="#B090E0" fontSize="10px">
              DevSecOps
            </text>
          </g>
          <g className="float-3" transform="translate(1050, 250)">
            <rect
              x="85"
              y="80"
              width="70"
              height="34"
              rx="4"
              fill="#0D1830"
              stroke="#00FFB3"
              strokeWidth="0.8"
              opacity="0.9"
            />
            <text x="120" y="102" textAnchor="middle" className="tag-text" fill="#00FFB3" fontSize="10px">
              AI / ML
            </text>
          </g>
          <g className="float-1" transform="translate(1050, 250)">
            <rect
              x="-165"
              y="72"
              width="82"
              height="34"
              rx="4"
              fill="#0D1830"
              stroke="#FF6B35"
              strokeWidth="0.8"
              opacity="0.9"
            />
            <text x="-124" y="94" textAnchor="middle" className="tag-text" fill="#FF8C5A" fontSize="10px">
              Blockchain
            </text>
          </g>

          {/* Connector dashes to tags */}
          <line
            x1="1106"
            y1="185"
            x2="1130"
            y2="165"
            stroke="#00D4FF"
            strokeWidth="0.5"
            opacity="0.5"
            strokeDasharray="3 3"
          />
          <line
            x1="994"
            y1="185"
            x2="960"
            y2="167"
            stroke="#7B5EA7"
            strokeWidth="0.5"
            opacity="0.5"
            strokeDasharray="3 3"
          />
          <line
            x1="1106"
            y1="315"
            x2="1130"
            y2="340"
            stroke="#00FFB3"
            strokeWidth="0.5"
            opacity="0.5"
            strokeDasharray="3 3"
          />
          <line
            x1="994"
            y1="315"
            x2="965"
            y2="330"
            stroke="#FF6B35"
            strokeWidth="0.5"
            opacity="0.5"
            strokeDasharray="3 3"
          />

          {/* Right edge circuits */}
          <path className="circuit-path c1 dash-flow-slow" d="M 1400 150 L 1320 150 L 1280 110 L 1200 110" />
          <path
            className="circuit-path c2 dash-flow"
            d="M 1400 380 L 1310 380 L 1270 420 L 1190 420 L 1150 380 L 1190 340"
          />
          <path className="circuit-path c3" d="M 1200 110 L 1200 80 L 1160 80" strokeWidth="0.8" opacity="0.5" />
          <circle className="pulse-dot-3 node-dot" cx="1280" cy="110" r="3.5" fill="#00D4FF" opacity="0.8" />
          <circle className="pulse-dot node-dot" cx="1200" cy="110" r="4" fill="#7B5EA7" filter="url(#glow-cyan)" />
          <circle className="pulse-dot-2 node-dot" cx="1150" cy="380" r="3.5" fill="#00D4FF" />

          {/* Scan line sweep */}
          <rect className="scan-line" x="0" y="0" width="1400" height="2" fill="url(#line-grad-h)" opacity="0.5" />

          {/* Floating ambient particles */}
          <circle cx="170" cy="60" r="1.5" fill="#00D4FF" opacity="0.5" className="float-2" />
          <circle cx="350" cy="430" r="1" fill="#00FFB3" opacity="0.4" className="float-1" />
          <circle cx="470" cy="90" r="2" fill="#7B5EA7" opacity="0.5" className="float-3" />
          <circle cx="630" cy="460" r="1.5" fill="#00D4FF" opacity="0.3" className="float-2" />
          <circle cx="1230" cy="50" r="1.5" fill="#00FFB3" opacity="0.5" className="float-1" />
          <circle cx="1350" cy="300" r="2" fill="#7B5EA7" opacity="0.4" className="float-3" />
          <circle cx="1290" cy="470" r="1" fill="#00D4FF" opacity="0.4" className="float-2" />

          {/* Animated data-stream dots moving along circuit paths */}
          <circle r="2.5" fill="#00D4FF" opacity="0.9" filter="url(#glow-cyan)">
            <animateMotion dur="3s" repeatCount="indefinite" path="M 0 180 L 80 180 L 120 140 L 250 140" />
          </circle>
          <circle r="2" fill="#7B5EA7" opacity="0.8">
            <animateMotion
              dur="4s"
              repeatCount="indefinite"
              path="M 0 320 L 60 320 L 100 360 L 180 360 L 220 320 L 350 320"
            />
          </circle>
          <circle r="2" fill="#00FFB3" opacity="0.7">
            <animateMotion
              dur="3.5s"
              repeatCount="indefinite"
              begin="1s"
              path="M 1400 150 L 1320 150 L 1280 110 L 1200 110"
            />
          </circle>

          {/* ── TYPOGRAPHY ── */}
          <g className="fade-in-1">
            <rect
              x="380"
              y="140"
              width="200"
              height="22"
              rx="3"
              fill="none"
              stroke="#00D4FF"
              strokeWidth="0.8"
              opacity="0.5"
            />
            <text
              x="480"
              y="155"
              textAnchor="middle"
              className="tag-text"
              fontSize="10.5px"
              letterSpacing="2"
              opacity="0.85"
            >
              TECHNOLOGY SERVICES
            </text>
          </g>

          <g className="fade-in-2">
            <text
              x="380"
              y="220"
              className="glow-text"
              fontSize="54"
              fontWeight="700"
              letterSpacing="-1"
              fill="url(#title-grad)"
              filter="url(#text-glow)"
            >
              Innovate.
            </text>
            <text
              x="380"
              y="282"
              className="glow-text"
              fontSize="54"
              fontWeight="700"
              letterSpacing="-1"
              fill="url(#title-grad)"
            >
              Transform.
            </text>
            <text x="380" y="344" className="glow-text" fontSize="54" fontWeight="700" letterSpacing="-1">
              <tspan fill="#FFFFFF">Deliver</tspan>
              <tspan fill="#00D4FF">.</tspan>
            </text>
          </g>

          <g className="fade-in-3">
            <text x="380" y="382" className="sub-text" fontSize="15" fontWeight="400" letterSpacing="0.3">
              End-to-end digital solutions for the modern enterprise.
            </text>
            <line x1="380" y1="400" x2="660" y2="400" stroke="url(#line-grad-h)" strokeWidth="1.5" />

            {/* CTA Buttons */}
            <rect
              style={{ cursor: 'pointer' }}
              onClick={() => navigate('services')}
              x="380"
              y="416"
              width="148"
              height="38"
              rx="4"
              fill="#00D4FF"
              fillOpacity="0.12"
              stroke="#00D4FF"
              strokeWidth="1"
            />
            <text
              style={{ cursor: 'pointer' }}
              onClick={() => navigate('services')}
              x="454"
              y="440"
              textAnchor="middle"
              className="glow-text"
              fontSize="12.5"
              fontWeight="600"
              fill="#00D4FF"
              letterSpacing="1"
            >
              EXPLORE →
            </text>
            <rect
              style={{ cursor: 'pointer' }}
              onClick={() => navigate('about')}
              x="544"
              y="416"
              width="130"
              height="38"
              rx="4"
              fill="none"
              stroke="#7B5EA7"
              strokeWidth="1"
              opacity="0.7"
            />
            <text
              style={{ cursor: 'pointer' }}
              onClick={() => navigate('about')}
              x="609"
              y="440"
              textAnchor="middle"
              className="sub-text"
              fontSize="12.5"
              fontWeight="500"
              letterSpacing="1"
            >
              GET IN TOUCH
            </text>
          </g>

          {/* ── BOTTOM STATS ROW ── */}
          <g className="fade-in-3">
            <line x1="0" y1="475" x2="1400" y2="475" stroke="#1E2A45" strokeWidth="0.5" />

            <text
              x="80"
              y="498"
              textAnchor="middle"
              className="glow-text"
              fontSize="13"
              fontWeight="600"
              fill="#00D4FF"
            >
              500+
            </text>
            <text x="80" y="514" textAnchor="middle" className="sub-text" fontSize="9.5" opacity="0.6">
              CLIENTS SERVED
            </text>
            <line x1="160" y1="477" x2="160" y2="520" stroke="#1E2A45" strokeWidth="0.5" />

            <text
              x="240"
              y="498"
              textAnchor="middle"
              className="glow-text"
              fontSize="13"
              fontWeight="600"
              fill="#00D4FF"
            >
              20+
            </text>
            <text x="240" y="514" textAnchor="middle" className="sub-text" fontSize="9.5" opacity="0.6">
              COUNTRIES
            </text>
            <line x1="320" y1="477" x2="320" y2="520" stroke="#1E2A45" strokeWidth="0.5" />

            <text
              x="420"
              y="498"
              textAnchor="middle"
              className="glow-text"
              fontSize="13"
              fontWeight="600"
              fill="#00D4FF"
            >
              99.9%
            </text>
            <text x="420" y="514" textAnchor="middle" className="sub-text" fontSize="9.5" opacity="0.6">
              UPTIME SLA
            </text>
          </g>
        </g>
      </svg>
    </section>
  );
};

// ─── HOME PAGE ───────────────────────────────────────────────────────────────

function HomePage({ navigate }) {
  const width = useWindowWidth();
  const isMobile = width < 768;

  return (
    <>
      {/* Hero */}
      <section
        style={{
          paddingTop: '68px',
          display: 'flex',
          alignItems: 'center',
          background: '#fafafa',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage:
              'linear-gradient(#e8e8e8 1px, transparent 1px), linear-gradient(90deg, #e8e8e8 1px, transparent 1px)',
            backgroundSize: '60px 60px',
            opacity: 0.35,
          }}
        /> */}
        {/* {!isMobile && (
          <div
            style={{
              position: 'absolute',
              top: 60,
              right: 0,
              width: '55%',
              height: '100%',
              background: 'linear-gradient(135deg, #f5f7ff 0%, #edf0ff 100%)',
              clipPath: 'polygon(8% 0, 100% 0, 100% 100%, 0 100%)',
            }}
          >
            <TechBanner />
          
            </div>
        )} */}
        <TechBanner navigate={navigate} />
        {/* <div
          className="container"
          style={{ position: 'relative', zIndex: 1, paddingTop: '60px', paddingBottom: isMobile ? '140px' : '188px' }}
        >
          
          <div style={{ maxWidth: '640px' }}>
            <div style={{ display: 'inline-block', background: '#f0f0f0', padding: '5px 14px', marginBottom: '28px' }}>
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: '600',
                  letterSpacing: '2px',
                  textTransform: 'uppercase',
                  color: '#6b7280',
                }}
              >
                Technology Services & Consulting
              </span>
            </div>
            <h1
              style={{
                fontSize: 'clamp(32px, 5.5vw, 52px)',
                fontWeight: '800',
                lineHeight: '1.05',
                letterSpacing: isMobile ? '-1px' : '-2px',
                color: '#0a0a0a',
                marginBottom: '28px',
              }}
            >
              Where strategy
              <br />
              meets <span style={{ color: '#1a56db' }}>technology.</span>
            </h1>
            <p
              style={{
                fontSize: '15px',
                lineHeight: '1.75',
                color: '#6b7280',
                maxWidth: '480px',
                marginBottom: '44px',
                fontWeight: '400',
              }}
            >
              We help the world's most ambitious enterprises navigate complexity, adopt emerging technology, and build
              the capabilities needed to lead in every market cycle.
            </p>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <button className="btn-primary" onClick={() => navigate('services')}>
                Explore Services →
              </button>
              <button className="btn-outline" onClick={() => navigate('about')}>
                Our Story
              </button>
            </div>
          </div>
        </div> */}
        {/* Stats bar */}
        {/* <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: '#0a0a0a', zIndex: 1 }}>
          <div className="container" style={{ padding: '24px 40px' }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
                gap: '16px',
              }}
            >
              {STATS.map((s, i) => (
                <div key={i} style={{ textAlign: 'center', padding: isMobile ? '8px 0' : '0' }}>
                  <div
                    style={{
                      fontSize: isMobile ? '22px' : '28px',
                      fontWeight: '700',
                      color: '#fff',
                      letterSpacing: '-1px',
                    }}
                  >
                    {s.value}
                  </div>
                  <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px', fontWeight: '400' }}>
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div> */}
      </section>

      {/* Services Preview */}
      <section style={{ background: '#fff', padding: isMobile ? '60px 0' : '70px 0' }}>
        <div className="container">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: isMobile ? 'flex-start' : 'flex-end',
              marginBottom: '48px',
              flexWrap: 'wrap',
              gap: '16px',
            }}
          >
            <div>
              <div className="section-label">What We Do</div>
              <h2 className="section-title">
                Services built for
                <br />
                enterprise scale
              </h2>
            </div>
            <button className="btn-outline" onClick={() => navigate('services')}>
              All Services →
            </button>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : width < 1024 ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
              gap: '1px',
              background: '#e8e8e8',
              border: '1px solid #e8e8e8',
            }}
          >
            {ALL_SERVICES.slice(0, isMobile ? 4 : 6).map((s, i) => (
              <ServiceCard key={i} s={s} i={i} />
            ))}
          </div>
        </div>
      </section>

      {/* Why Techsolist */}
      <section style={{ background: '#f9f9f9', padding: isMobile ? '60px 0' : '110px 0' }}>
        <div className="container">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
              gap: isMobile ? '40px' : '80px',
              alignItems: 'center',
            }}
          >
            <div>
              <div className="section-label">Why Techsolist</div>
              <h2 className="section-title" style={{ marginBottom: '24px' }}>
                Built for the
                <br />
                complexity ahead.
              </h2>
              <p style={{ fontSize: '15px', lineHeight: '1.75', color: '#6b7280', marginBottom: '36px' }}>
                In a world where every organization is a technology organization, the difference between leaders and
                laggards is execution. We bring the expertise, the tools, and the people to close that gap—at any scale,
                in any industry.
              </p>
              <button className="btn-primary" onClick={() => navigate('about')}>
                About Us →
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              {[
                { n: '50+', l: 'Projects delivered' },
                { n: '30+', l: 'Enterprise clients' },
                { n: '15+', l: 'Tech partnerships' },
                { n: '100%', l: 'On-time delivery rate' },
              ].map((item, i) => (
                <div key={i} style={{ padding: '28px 24px', background: '#fff', border: '1px solid #e8e8e8' }}>
                  <div
                    style={{
                      fontSize: '30px',
                      fontWeight: '700',
                      color: '#0a0a0a',
                      letterSpacing: '-1px',
                      lineHeight: '1',
                    }}
                  >
                    {item.n}
                  </div>
                  <div style={{ fontSize: '13px', color: '#9ca3af', marginTop: '8px', fontWeight: '400' }}>
                    {item.l}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section style={{ background: '#fff', padding: isMobile ? '60px 0' : '110px 0' }}>
        <div className="container">
          <div style={{ marginBottom: '48px' }}>
            <div className="section-label">Client Impact</div>
            <h2 className="section-title">Transformation at scale</h2>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : width < 1024 ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
              gap: '20px',
            }}
          >
            {CASE_STUDIES.map((cs, i) => (
              <div
                key={i}
                style={{
                  background: '#f9f9f9',
                  border: '1px solid #e8e8e8',
                  padding: '36px 32px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#fff';
                  e.currentTarget.style.boxShadow = '0 12px 40px rgba(0,0,0,0.07)';
                  e.currentTarget.style.transform = 'translateY(-3px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#f9f9f9';
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div
                  style={{
                    fontSize: '14px',
                    fontWeight: '700',
                    letterSpacing: '2px',
                    textTransform: 'uppercase',
                    color: '#9ca3af',
                    marginBottom: '16px',
                  }}
                >
                  {cs.industry}
                </div>
                <h3
                  style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#0a0a0a',
                    lineHeight: '1.4',
                    marginBottom: '24px',
                  }}
                >
                  {cs.title}
                </h3>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '28px' }}>
                  {cs.tags.map((t, j) => (
                    <span
                      key={j}
                      style={{
                        fontSize: '11px',
                        fontWeight: '500',
                        padding: '3px 10px',
                        background: '#f0f0f0',
                        color: '#6b7280',
                      }}
                    >
                      {t}
                    </span>
                  ))}
                </div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#1a56db' }}>Read case study →</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Insights */}
      <section style={{ background: '#f9f9f9', padding: isMobile ? '60px 0' : '110px 0' }}>
        <div className="container">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
              marginBottom: '48px',
              flexWrap: 'wrap',
              gap: '16px',
            }}
          >
            <div>
              <div className="section-label">Latest Thinking</div>
              <h2 className="section-title">Insights & Research</h2>
            </div>
            <button className="btn-outline">View All →</button>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : width < 1024 ? '1fr 1fr' : '1.5fr 1fr 1fr',
              gap: '28px',
            }}
          >
            {INSIGHTS.map((ins, i) => (
              <div key={i} style={{ borderTop: '2px solid #0a0a0a', paddingTop: '24px', cursor: 'pointer' }}>
                <div
                  style={{
                    fontSize: '14px',
                    fontWeight: '700',
                    letterSpacing: '1.5px',
                    textTransform: 'uppercase',
                    color: '#9ca3af',
                    marginBottom: '14px',
                  }}
                >
                  {ins.category}
                </div>
                <h3
                  style={{
                    fontSize: i === 0 && !isMobile ? '20px' : '18px',
                    fontWeight: '600',
                    color: '#0a0a0a',
                    lineHeight: '1.4',
                    marginBottom: '20px',
                  }}
                >
                  {ins.title}
                </h3>
                <div style={{ fontSize: '12px', color: '#9ca3af', display: 'flex', gap: '12px' }}>
                  <span>{ins.date}</span>
                  <span>·</span>
                  <span>{ins.readTime} read</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners */}
      <section
        style={{
          background: '#fff',
          padding: '60px 0',
          borderTop: '1px solid #e8e8e8',
          borderBottom: '1px solid #e8e8e8',
        }}
      >
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '36px' }}>
            <span
              style={{
                fontSize: '12px',
                fontWeight: '600',
                letterSpacing: '2px',
                textTransform: 'uppercase',
                color: '#9ca3af',
              }}
            >
              Trusted by industry leaders
            </span>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: isMobile ? '24px' : '56px',
              flexWrap: 'wrap',
              opacity: 0.35,
            }}
          >
            {['Goldman Sachs', 'NHS', 'Shell', 'Walmart', 'Siemens', 'JPMorgan'].map((name, i) => (
              <div
                key={i}
                style={{
                  fontSize: isMobile ? '14px' : '17px',
                  fontWeight: '700',
                  letterSpacing: '-0.5px',
                  color: '#0a0a0a',
                  whiteSpace: 'nowrap',
                }}
              >
                {name}
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTABanner navigate={navigate} />
    </>
  );
}

// ─── SERVICES PAGE ────────────────────────────────────────────────────────────

function ServicesPage({ navigate }) {
  const width = useWindowWidth();
  const isMobile = width < 768;
  const [expandedService, setExpandedService] = useState(null);

  return (
    <>
      <section style={{ paddingTop: '68px', background: '#0a0a0a', padding: '100px 0 80px' }}>
        <div className="container">
          <div style={{ maxWidth: '640px' }}>
            <div
              style={{
                fontSize: '11px',
                fontWeight: '700',
                letterSpacing: '2.5px',
                textTransform: 'uppercase',
                color: '#6b7280',
                marginBottom: '20px',
              }}
            >
              Our Services
            </div>
            <h1
              style={{
                fontSize: 'clamp(30px, 5vw, 60px)',
                fontWeight: '800',
                lineHeight: '1.08',
                letterSpacing: isMobile ? '-1px' : '-1.5px',
                color: '#fff',
                marginBottom: '24px',
              }}
            >
              Capabilities built
              <br />
              for what's next.
            </h1>
            <p style={{ fontSize: '15px', lineHeight: '1.75', color: '#9ca3af', maxWidth: '480px' }}>
              Nine integrated service lines. One seamless delivery model. Built to solve the most complex challenges
              enterprises face today—and tomorrow.
            </p>
          </div>
        </div>
      </section>

      {/* All services grid */}
      <section style={{ background: '#fff', padding: '80px 0 60px' }}>
        <div className="container">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : width < 1024 ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
              gap: '1px',
              background: '#e8e8e8',
              border: '1px solid #e8e8e8',
              marginBottom: '80px',
            }}
          >
            {ALL_SERVICES.map((s, i) => (
              <ServiceCard key={i} s={s} i={i} />
            ))}
          </div>
        </div>
      </section>

      {/* Expanded service detail spotlight */}
      <section style={{ background: '#f9f9f9', padding: '80px 0' }}>
        <div className="container">
          <div style={{ marginBottom: '56px' }}>
            <div className="section-label">Deep Dive</div>
            <h2 className="section-title">
              What each service
              <br />
              actually delivers
            </h2>
          </div>
          {ALL_SERVICES.map((s, i) => {
            const isOpen = expandedService === i;
            return (
              <div key={i} style={{ borderTop: '1px solid #e8e8e8' }}>
                {/* Service header row */}
                <div
                  onClick={() => setExpandedService(isOpen ? null : i)}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr auto' : '60px 1fr auto',
                    gap: '20px',
                    alignItems: 'center',
                    padding: '32px 0',
                    cursor: 'pointer',
                  }}
                >
                  {!isMobile && <div style={{ fontSize: '28px', color: '#1a56db' }}>{s.icon}</div>}
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                      {isMobile && <span style={{ fontSize: '20px', color: '#1a56db' }}>{s.icon}</span>}
                      <h3 style={{ fontSize: isMobile ? '17px' : '20px', fontWeight: '700', color: '#0a0a0a' }}>
                        {s.title}
                      </h3>
                    </div>
                    <p style={{ fontSize: '13.5px', color: '#9ca3af', marginTop: '4px' }}>{s.short}</p>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '10px' }}>
                      {s.tags.map((tag, j) => (
                        <span
                          key={j}
                          style={{
                            fontSize: '10px',
                            fontWeight: '600',
                            padding: '3px 10px',
                            background: '#f0f0f0',
                            color: '#6b7280',
                          }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div
                    style={{
                      width: '36px',
                      height: '36px',
                      border: '1.5px solid #e8e8e8',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s',
                      background: isOpen ? '#0a0a0a' : '#fff',
                      flexShrink: 0,
                    }}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 12 12"
                      fill="none"
                      style={{ transition: 'transform 0.25s', transform: isOpen ? 'rotate(180deg)' : 'rotate(0)' }}
                    >
                      <path
                        d="M2 4l4 4 4-4"
                        stroke={isOpen ? '#fff' : '#0a0a0a'}
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </div>

                {/* Expanded panel */}
                {isOpen && (
                  <div style={{ paddingBottom: '40px' }}>
                    <p
                      style={{
                        fontSize: '15px',
                        lineHeight: '1.8',
                        color: '#6b7280',
                        marginBottom: '36px',
                        maxWidth: '700px',
                      }}
                    >
                      {s.desc}
                    </p>

                    {/* Detail cards */}
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                        gap: '16px',
                        marginBottom: '36px',
                      }}
                    >
                      {s.details.map((d, di) => (
                        <div key={di} style={{ background: '#fff', border: '1px solid #e8e8e8', padding: '28px 24px' }}>
                          <div style={{ width: '6px', height: '6px', background: '#1a56db', marginBottom: '14px' }} />
                          <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#0a0a0a', marginBottom: '10px' }}>
                            {d.heading}
                          </h4>
                          <p style={{ fontSize: '13px', lineHeight: '1.7', color: '#6b7280' }}>{d.body}</p>
                        </div>
                      ))}
                    </div>

                    {/* Outcomes */}
                    <div style={{ background: '#0a0a0a', padding: '28px 32px' }}>
                      <div
                        style={{
                          fontSize: '11px',
                          fontWeight: '700',
                          letterSpacing: '2px',
                          textTransform: 'uppercase',
                          color: '#6b7280',
                          marginBottom: '16px',
                        }}
                      >
                        Typical Outcomes
                      </div>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                          gap: '10px',
                        }}
                      >
                        {s.outcomes.map((o, oi) => (
                          <div key={oi} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                            <span style={{ color: '#1a56db', fontSize: '14px', flexShrink: 0, marginTop: '1px' }}>
                              ✓
                            </span>
                            <span style={{ fontSize: '13px', color: '#9ca3af', lineHeight: '1.5' }}>{o}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div style={{ marginTop: '24px' }}>
                      <button className="btn-primary" onClick={() => navigate('contact')}>
                        Discuss This Service →
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Approach */}
      <section style={{ background: '#fff', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '56px' }}>
            <div className="section-label">Our Approach</div>
            <h2 className="section-title">How we deliver</h2>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
              gap: '1px',
              background: '#e8e8e8',
              border: '1px solid #e8e8e8',
            }}
          >
            {[
              {
                step: '01',
                title: 'Discover',
                desc: 'We immerse in your business to uncover root challenges and untapped opportunities.',
              },
              {
                step: '02',
                title: 'Design',
                desc: 'Co-create a tailored blueprint that balances ambition with pragmatic execution.',
              },
              {
                step: '03',
                title: 'Deliver',
                desc: 'Agile, phased delivery with continuous measurement and transparent reporting.',
              },
              {
                step: '04',
                title: 'Sustain',
                desc: 'Ongoing optimization, training, and managed services to protect long-term value.',
              },
            ].map((phase, i) => (
              <div key={i} style={{ background: '#fff', padding: isMobile ? '32px 24px' : '48px 36px' }}>
                <div
                  style={{
                    fontSize: '11px',
                    fontWeight: '700',
                    letterSpacing: '2px',
                    color: '#9ca3af',
                    marginBottom: '16px',
                  }}
                >
                  {phase.step}
                </div>
                <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#0a0a0a', marginBottom: '12px' }}>
                  {phase.title}
                </h3>
                <p style={{ fontSize: '13px', lineHeight: '1.7', color: '#9ca3af' }}>{phase.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTABanner navigate={navigate} />
    </>
  );
}

// ─── ABOUT PAGE ───────────────────────────────────────────────────────────────

function AboutPage({ navigate }) {
  const width = useWindowWidth();
  const isMobile = width < 768;

  return (
    <>
      <section style={{ paddingTop: '68px', background: '#f9f9f9', padding: '100px 0 80px' }}>
        <div className="container">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
              gap: isMobile ? '40px' : '80px',
              alignItems: 'center',
            }}
          >
            <div>
              <div className="section-label">About Techsolist</div>
              <h1
                style={{
                  fontSize: 'clamp(28px, 5vw, 58px)',
                  fontWeight: '800',
                  lineHeight: '1.08',
                  letterSpacing: isMobile ? '-1px' : '-1.5px',
                  color: '#0a0a0a',
                  marginBottom: '24px',
                }}
              >
                We believe technology should serve ambition.
              </h1>
              <p style={{ fontSize: '15px', lineHeight: '1.8', color: '#6b7280' }}>
                Founded with a clear mission: make enterprise-grade technology services accessible to ambitious
                organizations everywhere. Techsolist is a fast-growing IT solutions company delivering digital
                transformation, cloud, AI, and cybersecurity services to clients across financial services, healthcare,
                retail, and government.
              </p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              {STATS.map((s, i) => (
                <div key={i} style={{ background: '#fff', border: '1px solid #e8e8e8', padding: '28px 24px' }}>
                  <div style={{ fontSize: '28px', fontWeight: '700', color: '#0a0a0a', letterSpacing: '-1px' }}>
                    {s.value}
                  </div>
                  <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '6px' }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section style={{ background: '#0a0a0a', padding: '80px 0' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <div
              style={{
                fontSize: '11px',
                fontWeight: '700',
                letterSpacing: '2.5px',
                textTransform: 'uppercase',
                color: '#6b7280',
                marginBottom: '24px',
              }}
            >
              Our Mission
            </div>
            <p
              style={{
                fontSize: 'clamp(18px, 3vw, 34px)',
                fontWeight: '600',
                lineHeight: '1.4',
                color: '#fff',
                letterSpacing: '-0.5px',
              }}
            >
              "To help the world's most consequential organizations harness technology to reinvent, compete, and lead."
            </p>
          </div>
        </div>
      </section>

      <section style={{ background: '#fff', padding: '80px 0' }}>
        <div className="container">
          <div style={{ marginBottom: '56px' }}>
            <div className="section-label">What We Stand For</div>
            <h2 className="section-title">Our values</h2>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
              gap: '1px',
              background: '#e8e8e8',
              border: '1px solid #e8e8e8',
            }}
          >
            {VALUES.map((v, i) => (
              <div key={i} style={{ background: '#fff', padding: isMobile ? '36px 28px' : '52px 48px' }}>
                <div
                  style={{
                    fontSize: '11px',
                    fontWeight: '700',
                    letterSpacing: '2px',
                    color: '#9ca3af',
                    marginBottom: '16px',
                  }}
                >
                  0{i + 1}
                </div>
                <h3 style={{ fontSize: '19px', fontWeight: '700', color: '#0a0a0a', marginBottom: '14px' }}>
                  {v.title}
                </h3>
                <p style={{ fontSize: '14px', lineHeight: '1.75', color: '#9ca3af' }}>{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ background: '#f9f9f9', padding: '80px 0' }}>
        <div className="container">
          <div style={{ marginBottom: '56px' }}>
            <div className="section-label">Leadership</div>
            <h2 className="section-title">
              The team behind
              <br />
              the firm
            </h2>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
              gap: '20px',
            }}
          >
            {TEAM.map((member, i) => (
              <div
                key={i}
                style={{
                  background: '#fff',
                  border: '1px solid #e8e8e8',
                  padding: isMobile ? '24px 20px' : '36px 28px',
                }}
              >
                <div
                  style={{
                    width: '48px',
                    height: '48px',
                    background: '#f3f3f3',
                    marginBottom: '18px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '16px',
                    fontWeight: '700',
                    color: '#9ca3af',
                  }}
                >
                  {member.name
                    .split(' ')
                    .map((n) => n[0])
                    .join('')}
                </div>
                <h3
                  style={{
                    fontSize: isMobile ? '13px' : '15px',
                    fontWeight: '700',
                    color: '#0a0a0a',
                    marginBottom: '4px',
                  }}
                >
                  {member.name}
                </h3>
                <div style={{ fontSize: '11px', fontWeight: '600', color: '#1a56db', marginBottom: '10px' }}>
                  {member.role}
                </div>
                <p style={{ fontSize: '11.5px', lineHeight: '1.6', color: '#9ca3af' }}>{member.exp}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ background: '#fff', padding: '80px 0' }}>
        <div className="container">
          <div style={{ marginBottom: '56px' }}>
            <div className="section-label">Our Journey</div>
            <h2 className="section-title">
              Years of
              <br />
              transformation
            </h2>
          </div>
          <div
            style={{
              borderLeft: '2px solid #e8e8e8',
              paddingLeft: '32px',
              display: 'flex',
              flexDirection: 'column',
              gap: '36px',
            }}
          >
            {[
              {
                year: '2004',
                event: 'Founded in New York as a boutique technology strategy consultancy with 12 professionals.',
              },
              { year: '2008', event: 'Expanded to London and Singapore. Launched managed services division.' },
              { year: '2013', event: 'Reached $1B revenue milestone. Acquired three specialist analytics firms.' },
              { year: '2018', event: 'Launched AI & Data Analytics practice. Opened 40 new offices globally.' },
              { year: '2022', event: 'Surpassed 300,000 employees worldwide. Named #1 in client satisfaction by ISG.' },
              {
                year: '2025',
                event: 'Launched Techsolist AI Labs. Expanded to 5 new markets and surpassed 30 enterprise clients.',
              },
            ].map((item, i) => (
              <div key={i} style={{ position: 'relative' }}>
                <div
                  style={{
                    position: 'absolute',
                    left: '-41px',
                    width: '16px',
                    height: '16px',
                    background: '#fff',
                    border: '2px solid #0a0a0a',
                    borderRadius: '50%',
                  }}
                />
                <div style={{ fontSize: '12px', fontWeight: '700', color: '#9ca3af', marginBottom: '6px' }}>
                  {item.year}
                </div>
                <p style={{ fontSize: '14px', color: '#374151', lineHeight: '1.6', maxWidth: '600px' }}>{item.event}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTABanner navigate={navigate} />
    </>
  );
}

// ─── CAREERS PAGE ─────────────────────────────────────────────────────────────

function CareersPage({ navigate }) {
  const width = useWindowWidth();
  const isMobile = width < 768;
  const [filter, setFilter] = useState('All');
  const depts = [
    'All',
    'Cloud & Infrastructure',
    'AI & Data Analytics',
    'Enterprise Software',
    'Cybersecurity',
    'Strategy',
    'Product Engineering',
  ];
  const filtered = filter === 'All' ? OPEN_ROLES : OPEN_ROLES.filter((r) => r.dept === filter);

  return (
    <>
      <section style={{ paddingTop: '68px', background: '#0a0a0a', padding: '100px 0 80px' }}>
        <div className="container">
          <div style={{ maxWidth: '600px' }}>
            <div
              style={{
                fontSize: '11px',
                fontWeight: '700',
                letterSpacing: '2.5px',
                textTransform: 'uppercase',
                color: '#6b7280',
                marginBottom: '20px',
              }}
            >
              Careers at Techsolist
            </div>
            <h1
              style={{
                fontSize: 'clamp(30px, 5vw, 60px)',
                fontWeight: '800',
                lineHeight: '1.08',
                letterSpacing: isMobile ? '-1px' : '-1.5px',
                color: '#fff',
                marginBottom: '24px',
              }}
            >
              Do the best work
              <br />
              of your career.
            </h1>
            <p style={{ fontSize: '15px', lineHeight: '1.75', color: '#9ca3af', marginBottom: '36px' }}>
              We hire exceptional people who are energized by hard problems, global scale, and the opportunity to help
              shape how technology transforms the world.
            </p>
            <button
              className="btn-primary"
              style={{ background: '#fff', color: '#0a0a0a' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#1a56db';
                e.currentTarget.style.color = '#fff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#fff';
                e.currentTarget.style.color = '#0a0a0a';
              }}
            >
              View Open Roles ↓
            </button>
          </div>
        </div>
      </section>

      <section style={{ background: '#f9f9f9', padding: '80px 0' }}>
        <div className="container">
          <div style={{ marginBottom: '48px' }}>
            <div className="section-label">Life at Techsolist</div>
            <h2 className="section-title">Why people choose us</h2>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : width < 1024 ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
              gap: '20px',
            }}
          >
            {[
              {
                title: 'Global Impact',
                desc: 'Work on engagements that reshape industries, governments, and communities across 120+ countries.',
              },
              {
                title: 'Career Velocity',
                desc: 'Clear progression paths, mentorship from world-class leaders, and unparalleled learning opportunities.',
              },
              {
                title: 'Flexible Work',
                desc: 'Hybrid and remote options designed to enable peak performance wherever you do your best thinking.',
              },
              {
                title: 'Competitive Rewards',
                desc: 'Market-leading compensation, equity participation, comprehensive health benefits, and generous leave.',
              },
              {
                title: 'Continuous Learning',
                desc: '60+ hours of structured learning annually, certification sponsorships, and access to Techsolist University.',
              },
              {
                title: 'Inclusive Culture',
                desc: '67 nationalities. Active DEI programs. Employee resource groups. A genuine meritocracy.',
              },
            ].map((item, i) => (
              <div key={i} style={{ background: '#fff', border: '1px solid #e8e8e8', padding: '32px 28px' }}>
                <div style={{ width: '8px', height: '8px', background: '#1a56db', marginBottom: '18px' }} />
                <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#0a0a0a', marginBottom: '10px' }}>
                  {item.title}
                </h3>
                <p style={{ fontSize: '13px', lineHeight: '1.7', color: '#9ca3af' }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ background: '#fff', padding: '80px 0' }}>
        <div className="container">
          <div style={{ marginBottom: '36px' }}>
            <div className="section-label">Open Positions</div>
            <h2 className="section-title">Find your role</h2>
          </div>
          {/* Filter */}
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '32px' }}>
            {depts.map((d) => (
              <button
                key={d}
                onClick={() => setFilter(d)}
                style={{
                  background: filter === d ? '#0a0a0a' : 'transparent',
                  border: `1.5px solid ${filter === d ? '#0a0a0a' : '#e8e8e8'}`,
                  color: filter === d ? '#fff' : '#6b7280',
                  padding: '7px 14px',
                  fontSize: '11.5px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  fontFamily: "'Poppins', sans-serif",
                }}
              >
                {d}
              </button>
            ))}
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1px',
              background: '#e8e8e8',
              border: '1px solid #e8e8e8',
            }}
          >
            {filtered.map((role, i) => (
              <div
                key={i}
                style={{
                  background: '#fff',
                  padding: isMobile ? '20px 20px' : '24px 32px',
                  display: isMobile ? 'block' : 'grid',
                  gridTemplateColumns: '1fr auto',
                  alignItems: 'center',
                  gap: '20px',
                  cursor: 'pointer',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = '#f9f9f9')}
                onMouseLeave={(e) => (e.currentTarget.style.background = '#fff')}
              >
                <div style={{ marginBottom: isMobile ? '14px' : '0' }}>
                  <div style={{ fontSize: '15px', fontWeight: '600', color: '#0a0a0a', marginBottom: '6px' }}>
                    {role.title}
                  </div>
                  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '12px', color: '#9ca3af' }}>{role.dept}</span>
                    <span style={{ fontSize: '12px', color: '#9ca3af' }}>{role.location}</span>
                    <span style={{ fontSize: '12px', color: '#9ca3af' }}>{role.type}</span>
                  </div>
                </div>
                <button className="btn-outline" style={{ padding: '8px 20px', fontSize: '12px', whiteSpace: 'nowrap' }}>
                  Apply →
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTABanner navigate={navigate} />
    </>
  );
}

// ─── CONTACT PAGE ─────────────────────────────────────────────────────────────

function ContactPage() {
  const width = useWindowWidth();
  const isMobile = width < 768;
  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    service: '',
    budget: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [activeOffice, setActiveOffice] = useState(0);

  const handleSubmit = () => {
    if (form.name && form.email && form.message) setSubmitted(true);
  };

  const InputStyle = {
    width: '100%',
    padding: '12px 16px',
    border: '1.5px solid #e8e8e8',
    fontSize: '14px',
    color: '#0a0a0a',
    outline: 'none',
    transition: 'border-color 0.15s',
    fontFamily: "'Poppins', sans-serif",
    background: '#fafafa',
    borderRadius: '0',
  };

  const LabelStyle = {
    display: 'block',
    fontSize: '11.5px',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '8px',
    letterSpacing: '0.3px',
  };

  return (
    <>
      {/* Hero */}
      <section
        style={{
          paddingTop: '68px',
          background: '#0a0a0a',
          padding: '100px 0 80px',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '30%',
            transform: 'translate(-50%,-50%)',
            width: '600px',
            height: '600px',
            background: 'radial-gradient(circle, rgba(26,86,219,0.15) 0%, transparent 65%)',
            pointerEvents: 'none',
          }}
        />
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ maxWidth: '640px' }}>
            <div className="section-label" style={{ color: '#4b7bf5' }}>
              Contact Us
            </div>
            <h1
              style={{
                fontSize: 'clamp(30px, 5vw, 58px)',
                fontWeight: '800',
                lineHeight: '1.08',
                letterSpacing: isMobile ? '-1px' : '-1.5px',
                color: '#fff',
                marginBottom: '20px',
              }}
            >
              Let's start a<br />
              conversation.
            </h1>
            <p style={{ fontSize: '15px', lineHeight: '1.75', color: '#9ca3af', maxWidth: '460px' }}>
              Tell us about your challenge and we'll connect you with the right team within one business day.
            </p>
          </div>
        </div>
      </section>

      {/* Contact options bar */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e8e8e8' }}>
        <div className="container">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
              gap: '0',
            }}
          >
            {[
              { icon: '📧', label: 'Email Us', value: 'info@techsolist.com' },
              { icon: '📞', label: 'Call Us', value: '+0 000 000 0000' },
              { icon: '💬', label: 'Live Chat', value: 'Available 24/7' },
              { icon: '📍', label: 'Visit Us', value: '4 Global Offices' },
            ].map((item, i) => (
              <div
                key={i}
                style={{
                  padding: isMobile ? '20px 16px' : '28px 24px',
                  borderRight: i < 3 && !isMobile ? '1px solid #e8e8e8' : 'none',
                  borderBottom: isMobile && i < 2 ? '1px solid #e8e8e8' : 'none',
                  cursor: 'pointer',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = '#f9f9f9')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <div style={{ fontSize: '20px', marginBottom: '8px' }}>{item.icon}</div>
                <div
                  style={{
                    fontSize: '11px',
                    fontWeight: '700',
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    color: '#9ca3af',
                    marginBottom: '4px',
                  }}
                >
                  {item.label}
                </div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#0a0a0a' }}>{item.value}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Form + Info */}
      <section style={{ background: '#f9f9f9', padding: '80px 0' }}>
        <div className="container">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1.4fr 1fr',
              gap: isMobile ? '48px' : '64px',
            }}
          >
            {/* Form */}
            <div>
              <div style={{ marginBottom: '32px' }}>
                <div className="section-label">Send a Message</div>
                <h2
                  style={{
                    fontSize: 'clamp(22px, 3vw, 34px)',
                    fontWeight: '700',
                    color: '#0a0a0a',
                    letterSpacing: '-0.5px',
                  }}
                >
                  Tell us about
                  <br />
                  your project
                </h2>
              </div>

              {submitted ? (
                <div
                  style={{ padding: '60px 40px', background: '#fff', border: '1px solid #e8e8e8', textAlign: 'center' }}
                >
                  <div
                    style={{
                      width: '60px',
                      height: '60px',
                      background: '#1a56db',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 20px',
                      fontSize: '24px',
                      color: '#fff',
                    }}
                  >
                    ✓
                  </div>
                  <h3 style={{ fontSize: '22px', fontWeight: '700', color: '#0a0a0a', marginBottom: '12px' }}>
                    Message received.
                  </h3>
                  <p
                    style={{
                      fontSize: '14px',
                      color: '#9ca3af',
                      lineHeight: '1.7',
                      maxWidth: '320px',
                      margin: '0 auto',
                    }}
                  >
                    Thank you for reaching out. A Techsolist representative will be in touch within one business day.
                  </p>
                  <button className="btn-primary" style={{ marginTop: '28px' }} onClick={() => setSubmitted(false)}>
                    Send Another Message
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                  {/* Name + Email */}
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
                    <div>
                      <label style={LabelStyle}>Full Name *</label>
                      <input
                        type="text"
                        placeholder=""
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        style={InputStyle}
                        onFocus={(e) => (e.target.style.borderColor = '#0a0a0a')}
                        onBlur={(e) => (e.target.style.borderColor = '#e8e8e8')}
                      />
                    </div>
                    <div>
                      <label style={LabelStyle}>Work Email *</label>
                      <input
                        type="email"
                        placeholder=""
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        style={InputStyle}
                        onFocus={(e) => (e.target.style.borderColor = '#0a0a0a')}
                        onBlur={(e) => (e.target.style.borderColor = '#e8e8e8')}
                      />
                    </div>
                  </div>

                  {/* Company + Phone */}
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
                    <div>
                      <label style={LabelStyle}>Company</label>
                      <input
                        type="text"
                        placeholder="Your organization"
                        value={form.company}
                        onChange={(e) => setForm({ ...form, company: e.target.value })}
                        style={InputStyle}
                        onFocus={(e) => (e.target.style.borderColor = '#0a0a0a')}
                        onBlur={(e) => (e.target.style.borderColor = '#e8e8e8')}
                      />
                    </div>
                    <div>
                      <label style={LabelStyle}>Phone Number</label>
                      <input
                        type="tel"
                        placeholder=""
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        style={InputStyle}
                        onFocus={(e) => (e.target.style.borderColor = '#0a0a0a')}
                        onBlur={(e) => (e.target.style.borderColor = '#e8e8e8')}
                      />
                    </div>
                  </div>

                  {/* Service + Budget */}
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
                    <div>
                      <label style={LabelStyle}>Service of Interest</label>
                      <select
                        value={form.service}
                        onChange={(e) => setForm({ ...form, service: e.target.value })}
                        style={{ ...InputStyle, color: form.service ? '#0a0a0a' : '#9ca3af', appearance: 'none' }}
                        onFocus={(e) => (e.target.style.borderColor = '#0a0a0a')}
                        onBlur={(e) => (e.target.style.borderColor = '#e8e8e8')}
                      >
                        <option value="">Select a service</option>
                        {ALL_SERVICES.map((s) => (
                          <option key={s.title} value={s.title}>
                            {s.title}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={LabelStyle}>Estimated Budget</label>
                      <select
                        value={form.budget}
                        onChange={(e) => setForm({ ...form, budget: e.target.value })}
                        style={{ ...InputStyle, color: form.budget ? '#0a0a0a' : '#9ca3af', appearance: 'none' }}
                        onFocus={(e) => (e.target.style.borderColor = '#0a0a0a')}
                        onBlur={(e) => (e.target.style.borderColor = '#e8e8e8')}
                      >
                        <option value="">Select range</option>
                        <option value="under-50k">Under $50K</option>
                        <option value="50k-250k">$50K – $250K</option>
                        <option value="250k-1m">$250K – $1M</option>
                        <option value="1m-5m">$1M – $5M</option>
                        <option value="5m-plus">$5M+</option>
                      </select>
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label style={LabelStyle}>How can we help? *</label>
                    <textarea
                      placeholder="Briefly describe your challenge, goals, or project scope..."
                      rows={5}
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      style={{ ...InputStyle, resize: 'vertical' }}
                      onFocus={(e) => (e.target.style.borderColor = '#0a0a0a')}
                      onBlur={(e) => (e.target.style.borderColor = '#e8e8e8')}
                    />
                  </div>

                  <p style={{ fontSize: '11.5px', color: '#9ca3af' }}>
                    By submitting, you agree to our Privacy Policy. We'll respond within one business day.
                  </p>

                  <div>
                    <button className="btn-primary" style={{ padding: '14px 36px' }} onClick={handleSubmit}>
                      Send Message →
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Side info */}
            <div>
              {/* Offices */}
              <div style={{ marginBottom: '40px' }}>
                <div className="section-label">Global Offices</div>
                <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#0a0a0a', marginBottom: '24px' }}>
                  Find us near you
                </h3>

                {/* Office tabs */}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '24px' }}>
                  {OFFICES.map((o, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveOffice(i)}
                      style={{
                        background: activeOffice === i ? '#0a0a0a' : 'transparent',
                        border: `1.5px solid ${activeOffice === i ? '#0a0a0a' : '#e8e8e8'}`,
                        color: activeOffice === i ? '#fff' : '#6b7280',
                        padding: '6px 14px',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                        fontFamily: "'Poppins', sans-serif",
                      }}
                    >
                      {o.city}
                    </button>
                  ))}
                </div>

                {/* Active office card */}
                <div style={{ background: '#fff', border: '1px solid #e8e8e8', padding: '28px 24px' }}>
                  <div style={{ fontSize: '16px', fontWeight: '700', color: '#0a0a0a', marginBottom: '8px' }}>
                    {OFFICES[activeOffice].city}
                  </div>
                  <div style={{ fontSize: '13.5px', color: '#6b7280', lineHeight: '1.6', marginBottom: '12px' }}>
                    {OFFICES[activeOffice].address}
                  </div>
                  <div style={{ fontSize: '13.5px', fontWeight: '600', color: '#1a56db' }}>
                    {OFFICES[activeOffice].phone}
                  </div>
                </div>
              </div>

              {/* FAQ */}
              <div style={{ marginBottom: '36px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#0a0a0a', marginBottom: '16px' }}>
                  Common Questions
                </h3>
                {[
                  {
                    q: 'How quickly will you respond?',
                    a: 'We aim to respond to all inquiries within one business day. For urgent matters, call us directly.',
                  },
                  {
                    q: 'Do you work with mid-market companies?',
                    a: 'Yes. While we specialize in enterprise, we take on select mid-market engagements that fit our expertise.',
                  },
                  {
                    q: 'Can I request an NDA before discussing my project?',
                    a: "Absolutely. We're happy to sign an NDA before any substantive project discussions.",
                  },
                ].map((faq, i) => (
                  <div
                    key={i}
                    style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: '16px', marginBottom: '16px' }}
                  >
                    <div style={{ fontSize: '13.5px', fontWeight: '600', color: '#0a0a0a', marginBottom: '6px' }}>
                      {faq.q}
                    </div>
                    <div style={{ fontSize: '12.5px', color: '#9ca3af', lineHeight: '1.6' }}>{faq.a}</div>
                  </div>
                ))}
              </div>

              {/* General contact box */}
              <div style={{ background: '#0a0a0a', padding: '28px 24px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#fff', marginBottom: '10px' }}>
                  General Inquiries
                </h3>
                <p style={{ fontSize: '12.5px', color: '#6b7280', lineHeight: '1.7', marginBottom: '14px' }}>
                  For media requests, partnerships, or general questions:
                </p>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#4b7bf5' }}>info@techsolist.com</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map-style section */}
      <section style={{ background: '#fff', padding: '0' }}>
        <div
          style={{
            background: 'linear-gradient(135deg, #f9f9f9 0%, #f0f2ff 100%)',
            padding: '60px 0',
            borderTop: '1px solid #e8e8e8',
          }}
        >
          <div className="container" style={{ textAlign: 'center' }}>
            <div className="section-label">Worldwide Presence</div>
            <h2
              style={{ fontSize: 'clamp(22px, 3vw, 36px)', fontWeight: '700', color: '#0a0a0a', marginBottom: '16px' }}
            >
              Operating in 50+ countries
            </h2>
            <p style={{ fontSize: '14px', color: '#9ca3af', marginBottom: '40px' }}>
              With delivery centers across 4 continents, we bring local expertise with global scale.
            </p>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
                gap: '16px',
                maxWidth: '800px',
                margin: '0 auto',
              }}
            >
              {OFFICES.map((o, i) => (
                <div
                  key={i}
                  style={{ background: '#fff', border: '1px solid #e8e8e8', padding: '24px 20px', textAlign: 'left' }}
                >
                  <div
                    style={{
                      width: '8px',
                      height: '8px',
                      background: '#1a56db',
                      borderRadius: '50%',
                      marginBottom: '12px',
                    }}
                  />
                  <div style={{ fontSize: '14px', fontWeight: '700', color: '#0a0a0a', marginBottom: '4px' }}>
                    {o.city}
                  </div>
                  <div style={{ fontSize: '11.5px', color: '#9ca3af', lineHeight: '1.5' }}>{o.address}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

// ─── FOOTER ───────────────────────────────────────────────────────────────────

function Footer({ navigate }) {
  const width = useWindowWidth();
  const isMobile = width < 768;
  const cols = [
    {
      title: 'Services',
      links: [
        'Digital Transformation',
        'Cloud & Infrastructure',
        'AI & Analytics',
        'Cybersecurity',
        'Enterprise Software',
        'Technology Strategy',
      ],
    },
    { title: 'Company', links: ['About Us', 'Leadership', 'Careers', 'Newsroom', 'Sustainability'] },
    { title: 'Contact', links: ['Get in Touch', 'Office Locations', 'Partner Network', 'Media Inquiries'] },
  ];

  return (
    <footer style={{ background: '#0a0a0a', borderTop: '1px solid #1a1a1a' }}>
      <div className="container" style={{ padding: '64px 40px 32px' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : width < 1024 ? '1fr 1fr' : '2fr 1fr 1fr 1fr',
            gap: isMobile ? '36px' : '48px',
            marginBottom: '48px',
          }}
        >
          <div>
            <div
              onClick={() => navigate('home')}
              style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px', cursor: 'pointer' }}
            >
              <svg width="22" height="22" viewBox="0 0 30 30" fill="none">
                <rect width="13" height="13" fill="#1a56db" />
                <rect x="17" width="13" height="13" fill="#fff" />
                <rect y="17" width="13" height="13" fill="#fff" />
                <rect x="17" y="17" width="13" height="13" fill="#1a56db" />
              </svg>
            </div>
            <p
              style={{ fontSize: '13px', color: '#4b5563', lineHeight: '1.7', maxWidth: '240px', marginBottom: '24px' }}
            >
              Helping the world's leading organizations harness technology to reinvent and grow.
            </p>
            <div style={{ display: 'flex', gap: '10px' }}>
              {['in', '𝕏', 'f'].map((icon, i) => (
                <div
                  key={i}
                  style={{
                    width: '34px',
                    height: '34px',
                    border: '1px solid #2a2a2a',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    fontSize: '12px',
                    color: '#6b7280',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#fff';
                    e.currentTarget.style.color = '#fff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#2a2a2a';
                    e.currentTarget.style.color = '#6b7280';
                  }}
                >
                  {icon}
                </div>
              ))}
            </div>
          </div>
          {cols.map((col, i) => (
            <div key={i}>
              <div
                style={{
                  fontSize: '11px',
                  fontWeight: '700',
                  color: '#fff',
                  letterSpacing: '2px',
                  textTransform: 'uppercase',
                  marginBottom: '16px',
                }}
              >
                {col.title}
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {col.links.map((link, j) => (
                  <li key={j} style={{ marginBottom: '9px' }}>
                    <span
                      style={{ fontSize: '13px', color: '#4b5563', cursor: 'pointer', transition: 'color 0.15s' }}
                      onMouseEnter={(e) => (e.currentTarget.style.color = '#fff')}
                      onMouseLeave={(e) => (e.currentTarget.style.color = '#4b5563')}
                    >
                      {link}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div
          style={{
            borderTop: '1px solid #1a1a1a',
            paddingTop: '24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '12px',
          }}
        >
          <span style={{ fontSize: '12px', color: '#4b5563' }}>
            © 2025 Techsolist Corporation. All rights reserved.
          </span>
          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
            {['Privacy Policy', 'Terms of Use', 'Cookie Settings'].map((l, i) => (
              <span
                key={i}
                style={{ fontSize: '12px', color: '#4b5563', cursor: 'pointer', transition: 'color 0.15s' }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#9ca3af')}
                onMouseLeave={(e) => (e.currentTarget.style.color = '#4b5563')}
              >
                {l}
              </span>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}

// ─── APP ─────────────────────────────────────────────────────────────────────

export default function App() {
  const [scrolled, setScrolled] = useState(false);
  const [currentPage, setCurrentPage] = useState('home');

  const navigate = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const renderPage = () => {
    switch (currentPage) {
      case 'home':
        return <HomePage navigate={navigate} />;
      case 'services':
        return <ServicesPage navigate={navigate} />;
      case 'about':
        return <AboutPage navigate={navigate} />;
      case 'careers':
        return <CareersPage navigate={navigate} />;
      case 'contact':
        return <ContactPage />;
      default:
        return <HomePage navigate={navigate} />;
    }
  };

  return (
    <>
      <GlobalStyles />
      <Header scrolled={scrolled} currentPage={currentPage} navigate={navigate} />
      <main>{renderPage()}</main>
      <Footer navigate={navigate} />
    </>
  );
}
